import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import BackOfficeMenu from '../components/BackOfficeMenu';
import {usePermissions} from '../../utils/usePermissions';
import {useProtectedDataLock} from '../../context/SensitiveDataLockContext';
import SensitiveDeleteModal from '../../components/SensitiveDeleteModal';

import {
  validateClientForm as validateClientFormUtil,
  validateDossierForm as validateDossierFormUtil,
  extractBackendError,
  mapBackendErrorToField
} from '../../utils/clientDossierValidator';
import { getStegOptionsList } from '../../utils/stegOptions';
import './ClientsManagement.css';
import Swal from 'sweetalert2';

const ClientsManagement = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const {isActionVisible} = useProtectedDataLock();
  const [clients, setClients] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmittingClient, setIsSubmittingClient] = useState(false);

  // États pour la suppression sensible
  const [showSensitiveDeleteModal, setShowSensitiveDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletionConfig, setDeletionConfig] = useState({ client: { enabled: true, mode: 'SOFT_DELETE' } });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // États pour les vues et modales
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'add'
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // État pour le formulaire
  const [formData, setFormData] = useState({
    // Informations client
    nom: '',
    prenom: '',
    typeIdentifiant: 'cin',
    cin: '',
    passeport: '',
    telephone: '',
    email: '',
    adresse: '',
    typeClient: 'BT',
    // Informations dossier (premier dossier)
    pompage: false,
    referenceSteg: '',
    puissance: '',
    district: '',
    moisAnnee: '',
    typePaiement: 'comptant',
    montantTotalPaiement: 0,
    montantSteg: 10000,
    acompteVerse: false,
    montantComptant: '',
    montantCredit: 0,
    nombreMoisCredit: 0,
    dateDebutCredit: '',
    modePaiementComptant: 'especes',
    datePaiementComptant: new Date().toISOString().split('T')[0],
    dateArrivDossier: '',
    dateDepotSteg: '',
    dateDemandeReception: '',
    dateLivraison: '',
    dateMiseEnService: '',
    dateDF: '',
    commercial: '',
    installateur: '',
    etatDossier: 'Déposé chez Sastra',
    bl: '',
    facture: '',
    dateFacture: '',
    note: '',
    packId: ''
  });

  // État pour l'import Excel
  const [excelFile, setExcelFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // ── Helpers pour les selects relationnels ─────────────────────
  const [commerciauxList, setCommerciauxList] = useState([]);
  const [installateursList, setInstallateursList] = useState([]);
  const [stegsByCategorie, setStegsByCategorie] = useState([]);
  const [allStegs, setAllStegs] = useState([]);
  const [packsList, setPacksList] = useState([]);
  const [loadingSteg, setLoadingSteg] = useState(false);
  const [commercialAddSearch, setCommercialAddSearch] = useState('');

  const CATEGORIES_STEG = [
    'Région de Tunis', 'Région Nord', 'Région Nord-Ouest',
    'Région Centre', 'Région Sfax', 'Région Sud-Ouest', 'Région Sud',
  ];

  const MOIS_NOMS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const currentMonth = MOIS_NOMS[new Date().getMonth()];
  const currentYear = new Date().getFullYear().toString();

  // État pour les filtres
  const [filters, setFilters] = useState({
    typeClient: '',
    categorie: '',
    district: '',
    mois: '',
    annee: '',
    etatDossier: '',
    search: ''
  });

  // État pour la pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const clearMessages = () => {
    setError(null);
    setMessage(null);
  };

  const extractArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.packs)) return payload.packs;
    return [];
  };

  const nextStep = () => {
    if (currentStep === 1) {
      const { isValid, errors, errorSummary } = validateClientFormUtil(formData);
      const activeCinErr = fieldErrors?.cin;
      if (!isValid || activeCinErr) {
        const newErrors = { ...(errors || {}) };
        if (activeCinErr) newErrors.cin = activeCinErr;
        setFieldErrors(newErrors);
        setError(errorSummary || activeCinErr);
        return;
      }
    } else if (currentStep === 2) {
      const { isValid, errors, errorSummary } = validateDossierFormUtil(formData, { typeClient: formData.typeClient });
      const activeStegErr = fieldErrors?.referenceSteg;
      if (!isValid || activeStegErr) {
        const newErrors = { ...(errors || {}) };
        if (activeStegErr) newErrors.referenceSteg = activeStegErr;
        setFieldErrors(newErrors);
        setError(errorSummary || activeStegErr);
        return;
      }

      // Suggestion automatique du montant total pour l'étape 3
      const p = parseFloat(formData.puissance) || 0;
      let suggestion = 0;
      if (p > 0 && p <= 2) suggestion = 7500;
      else if (p > 2 && p <= 3) suggestion = 10000;

      if (suggestion > 0 && (!formData.montantTotalPaiement || Number(formData.montantTotalPaiement) === 0)) {
        setFormData(prev => ({...prev, montantTotalPaiement: suggestion}));
      }
    } else if (currentStep === 3) {
      const dossierVal = validateDossierFormUtil(formData, { typeClient: formData.typeClient, requireFinancial: true });
      if (!dossierVal.isValid) {
        setFieldErrors(dossierVal.errors || {});
        setError(Object.values(dossierVal.errors || {}).filter(Boolean).join('\n'));
        return;
      }
    }
    setError(null);
    setFieldErrors({});
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setError(null);
    setFieldErrors({});
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const API_URL = import.meta.env.VITE_API_URL;

  const normalizeDecimalInput = (value) => (value === null || value === undefined ? '' : String(value));

  const getApiUrl = (endpoint) => {
    const baseUrl = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;
    return `${baseUrl}${endpoint}`;
  };

  const authH = () => ({Authorization: `Bearer ${localStorage.getItem('token')}`});

  // Charge commerciaux + installateurs + config suppressions + packs au montage
  const fetchHelpers = useCallback(async () => {
    try {
      const [cRes, iRes, sRes, cfgRes, pRes] = await Promise.all([
        axios.get(getApiUrl('/clients/helpers/commerciaux'), {headers: authH()}),
        axios.get(getApiUrl('/clients/helpers/installateurs'), {headers: authH()}),
        axios.get(getApiUrl('/steg'), {headers: authH()}),
        axios.get(getApiUrl('/system-settings/deletion-config'), {headers: authH()}),
        axios.get(getApiUrl('/packs'), {headers: authH(), params: { status: 'actif', limit: 100 }})
      ]);
      setCommerciauxList(cRes.data?.data || []);
      setInstallateursList(iRes.data?.data || []);
      setAllStegs(extractArray(sRes.data));
      if (cfgRes.data?.data) {
        setDeletionConfig(cfgRes.data.data);
      }
      setPacksList(extractArray(pRes.data));
    } catch (_) { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePackSelectChange = (e) => {
    const pId = e.target.value;
    if (!pId) {
      setFormData(prev => ({
        ...prev,
        packId: ''
      }));
      return;
    }

    const selectedPack = packsList.find(p => String(p._id) === String(pId));
    if (!selectedPack) return;

    let rawPuissance = '';
    if (selectedPack.puissance) {
      const match = String(selectedPack.puissance).match(/[\d.]+/);
      if (match) rawPuissance = match[0];
    }

    const price = (selectedPack.prixFinal !== undefined && selectedPack.prixFinal !== null && Number(selectedPack.prixFinal) > 0)
      ? Number(selectedPack.prixFinal)
      : Number(selectedPack.prixCalcule || 0);

    setFormData(prev => ({
      ...prev,
      packId: selectedPack._id,
      puissance: rawPuissance || prev.puissance,
      montantTotalPaiement: price || prev.montantTotalPaiement
    }));
  };

  useEffect(() => {
    const userType = localStorage.getItem('typeUser');
    if (userType !== 'admin' && userType !== 'moderateur') {
      onNavigate('profile');
      return;
    }
    if (userType === 'moderateur' && !hasPermission('clients', 'read')) {
      onNavigate('dashboard');
      return;
    }
    fetchClients();
    fetchHelpers();
  }, [fetchHelpers]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchClients(1);
    }, filters.search ? 500 : 0);

    return () => clearTimeout(delayDebounceFn);
  }, [filters.typeClient, filters.categorie, filters.district, filters.mois, filters.annee, filters.etatDossier, filters.search]);

  // Real-time check CIN uniqueness
  useEffect(() => {
    if (showAddForm || showAddModal || showEditModal) {
      const isCinSelected = formData.typeIdentifiant === 'cin';
      const cinVal = (formData.cin || '').trim();

      if (isCinSelected && /^\d{8}$/.test(cinVal)) {
        const timer = setTimeout(async () => {
          try {
            const token = localStorage.getItem('token');
            const excludeId = showEditModal && selectedClient ? selectedClient._id : '';
            const res = await axios.get(getApiUrl(`/clients/search/cin/${cinVal}`), {
              headers: { Authorization: `Bearer ${token}` },
              params: excludeId ? { excludeClientId: excludeId } : {}
            });

            if (res.data?.exists) {
              setFieldErrors(prev => ({
                ...(prev || {}),
                cin: 'Ce CIN est déjà utilisé par un autre client.'
              }));
            } else {
              setFieldErrors(prev => {
                const copy = { ...(prev || {}) };
                if (copy.cin === 'Ce CIN est déjà utilisé par un autre client.') {
                  delete copy.cin;
                }
                return copy;
              });
            }
          } catch (_) {}
        }, 400);

        return () => clearTimeout(timer);
      } else {
        setFieldErrors(prev => {
          const copy = { ...(prev || {}) };
          if (copy.cin === 'Ce CIN est déjà utilisé par un autre client.') {
            delete copy.cin;
          }
          return copy;
        });
      }
    }
  }, [formData.cin, formData.typeIdentifiant, showAddForm, showAddModal, showEditModal, selectedClient]);

  // Real-time check Référence STEG uniqueness
  useEffect(() => {
    if (showAddForm || showAddModal || showEditModal) {
      const isPompage = !!formData.pompage;
      const stegVal = (formData.referenceSteg || '').trim();

      const isBt = formData.typeClient === 'BT' && /^\d{9}$/.test(stegVal);
      const isMt = formData.typeClient === 'MT' && /^\d{6}$/.test(stegVal);
      const isBtMt = formData.typeClient === 'BT/MT' && (/^\d{6}$/.test(stegVal) || /^\d{9}$/.test(stegVal));

      if (!isPompage && (isBt || isMt || isBtMt)) {
        const timer = setTimeout(async () => {
          try {
            const token = localStorage.getItem('token');
            const excludeDossierId = showEditModal && selectedClient?.dossiers?.[0]?._id ? selectedClient.dossiers[0]._id : '';
            const res = await axios.get(getApiUrl(`/clients/search/refsteg/${stegVal}`), {
              headers: { Authorization: `Bearer ${token}` },
              params: excludeDossierId ? { excludeDossierId } : {}
            });

            if (res.data?.exists) {
              setFieldErrors(prev => ({
                ...(prev || {}),
                referenceSteg: 'Cette référence STEG est déjà utilisée par un autre dossier.'
              }));
            } else {
              setFieldErrors(prev => {
                const copy = { ...(prev || {}) };
                if (copy.referenceSteg === 'Cette référence STEG est déjà utilisée par un autre dossier.') {
                  delete copy.referenceSteg;
                }
                return copy;
              });
            }
          } catch (_) {}
        }, 400);

        return () => clearTimeout(timer);
      } else {
        setFieldErrors(prev => {
          const copy = { ...(prev || {}) };
          if (copy.referenceSteg === 'Cette référence STEG est déjà utilisée par un autre dossier.') {
            delete copy.referenceSteg;
          }
          return copy;
        });
      }
    }
  }, [formData.referenceSteg, formData.pompage, formData.typeClient, showAddForm, showAddModal, showEditModal, selectedClient]);

  const fetchClients = async (page = pagination.page) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Token non trouvé. Veuillez vous reconnecter.');
        setLoading(false);
        return;
      }

      // Tous les filtres sont envoyés simultanément — le backend les combine avec AND.
      // Les valeurs vides ('') sont ignorées côté backend.
      const params = {
        page: page,
        limit: pagination.limit,
      };

      const searchTrimmed = (filters.search || '').trim();
      if (searchTrimmed) params.search = searchTrimmed;
      if (filters.categorie) params.categorie = filters.categorie;
      if (filters.district)  params.district  = filters.district;
      if (filters.typeClient) params.typeClient = filters.typeClient;
      if (filters.mois)       params.mois       = filters.mois;
      if (filters.annee)      params.annee      = filters.annee;
      if (filters.etatDossier) params.etatDossier = filters.etatDossier;

      const response = await axios.get(getApiUrl('/clients'), {
        headers: {Authorization: `Bearer ${token}`},
        params: params
      });
      setClients(response.data.data || response.data);
      setPagination({
        page: response.data.page || 1,
        limit: pagination.limit,
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 0
      });
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des clients:', err);
      if (err.response?.status === 403) {
        setError('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      } else if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else {
        setError('Erreur lors du chargement des clients');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatutColor = (statut) => {
    const colors = {
      'nouveau': '#3b82f6',
      'installe': '#10b981',
      'receptionne': '#8b5cf6'
    };
    return colors[statut] || '#6b7280';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'nouveau': 'Nouveau',
      'installe': 'Installé',
      'receptionne': 'Réceptionné'
    };
    return labels[statut] || statut;
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      nom: client.nom,
      prenom: client.prenom,
      typeIdentifiant: client.cin ? 'cin' : 'passeport',
      cin: client.cin || '',
      passeport: client.passeport || '',
      telephone: client.telephone,
      email: client.email || '',
      adresse: client.adresse || '',
      typeClient: client.typeClient || 'BT'
    });
    setShowEditModal(true);
  };

  const handleDelete = (client) => {
    setSelectedClient(client);
    setDeleteTarget({
      itemType: 'client',
      id: client._id,
      name: `${client.nom} ${client.prenom || ''}`.trim()
    });
    setShowSensitiveDeleteModal(true);
  };

  const handleConfirmSensitiveDelete = async (reasonArg, modeArg) => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      const reason = typeof reasonArg === 'object' ? reasonArg.reason : reasonArg;
      const mode = typeof reasonArg === 'object' ? reasonArg.mode : (modeArg || 'SOFT_DELETE');

      const response = await axios.delete(getApiUrl(`/clients/${deleteTarget.id}`), {
        data: { reason, mode },
        headers: authH()
      });

      if (response.data?.success) {
        setShowSensitiveDeleteModal(false);
        Swal.fire({
          icon: 'success',
          title: 'Action effectuée',
          text: response.data.message || 'Le client a été supprimé / archivé avec succès.',
          confirmButtonColor: '#1e3a5f'
        });
        fetchClients();
      }
    } catch (err) {
      console.error('Erreur suppression client:', err);
      const msg = err.response?.data?.message || 'Erreur lors de la suppression du client';
      Swal.fire('Erreur', msg, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDetail = (client) => {
    onNavigate('client-detail', client._id);
  };

  const handleAdd = () => {
    fetchHelpers();
    const today = new Date().toISOString().split('T')[0];
    const date = new Date();
    const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const moisAnneeDefaut = `${MOIS[date.getMonth()]} ${date.getFullYear()}`;
    setCurrentStep(1);
    clearMessages();
    setFieldErrors({});
    setFormData({
      nom: '',
      prenom: '',
      typeIdentifiant: 'cin',
      cin: '',
      passeport: '',
      telephone: '',
      email: '',
      adresse: '',
      typeClient: 'BT',
      pompage: false,
      referenceSteg: '',
      puissance: '',
      district: '',
      moisAnnee: moisAnneeDefaut,
      typePaiement: 'comptant',
      montantTotalPaiement: '',
      montantComptant: '',
      montantCredit: '',
      nombreMoisCredit: 0,
      dateDebutCredit: '',
      modePaiementComptant: 'especes',
      datePaiementComptant: today,
      dateArrivDossier: today,
      dateDepotSteg: '',
      dateDemandeReception: '',
      dateLivraison: '',
      dateMiseEnService: '',
      dateDF: '',
      commercial: '',
      installateur: '',
      etatDossier: 'Déposé chez Sastra',
      bl: '',
      facture: '',
      dateFacture: '',
      notes: '',
      packId: ''
    });
    setShowAddForm(prev => !prev);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setCurrentStep(1);
    clearMessages();
    setFieldErrors({});
    setCommercialAddSearch('');
  };

  const handleImport = () => {
    setExcelFile(null);
    setImportResult(null);
    setShowImportModal(true);
  };

  // Options d'exportation avec choix de filtres (statuts client, états dossier, période)
  const showExportOptions = () => {
    const etatsDossiersList = [
      'Déposé chez Sastra',
      'En attente d\'approbation bureau d\'étude',
      'Approuvé bureau d\'étude',
      'En attente d\'approbation',
      'En attente d\'installation',
      'En attente de réception',
      'Réceptionné',
      'Manque PV',
      'Dossier en facturation',
      'Annulé'
    ];

    const htmlContent = `
      <div style="text-align: left; font-size: 0.92rem; font-family: inherit;">
        
        <!-- Section 1 : Statuts de paiement -->
        <div style="margin-bottom: 16px;">
          <label style="font-weight: 700; color: #1e293b; display: block; margin-bottom: 6px;">
            💳 Statut de paiement du client :
          </label>
          <div style="display: flex; gap: 10px; flex-wrap: wrap;">
            <label style="display: flex; align-items: center; gap: 6px; background: #f1f5f9; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-weight: 600;">
              <input type="checkbox" class="export-statut-cb" value="paye" checked> Payé
            </label>
            <label style="display: flex; align-items: center; gap: 6px; background: #f1f5f9; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-weight: 600;">
              <input type="checkbox" class="export-statut-cb" value="partiel" checked> Partiellement payé
            </label>
            <label style="display: flex; align-items: center; gap: 6px; background: #f1f5f9; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-weight: 600;">
              <input type="checkbox" class="export-statut-cb" value="non_paye" checked> Non payé
            </label>
          </div>
        </div>

        <!-- Section 2 : États du dossier -->
        <div style="margin-bottom: 16px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
            <label style="font-weight: 700; color: #1e293b;">
              📁 États des dossiers à inclure :
            </label>
            <button type="button" id="toggle-all-etats" style="background: none; border: none; color: #2563eb; font-weight: 600; cursor: pointer; font-size: 0.82rem;">
              Tout cocher / décocher
            </button>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; max-height: 160px; overflow-y: auto; padding: 8px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc;">
            ${etatsDossiersList.map((etat) => `
              <label style="display: flex; align-items: center; gap: 6px; font-size: 0.83rem; cursor: pointer;">
                <input type="checkbox" class="export-etat-cb" value="${etat}" checked> ${etat}
              </label>
            `).join('')}
          </div>
        </div>

        <!-- Section 3 : Période temporelle -->
        <div style="margin-bottom: 12px;">
          <label style="font-weight: 700; color: #1e293b; display: block; margin-bottom: 6px;">
            📅 Période des données :
          </label>
          <select id="export-period-select" class="swal2-select" style="width: 100%; margin: 0; padding: 8px 12px; font-size: 0.9rem; border-radius: 6px;">
            <option value="current">Filtres actuels du tableau</option>
            <option value="week">Cette semaine (7 derniers jours)</option>
            <option value="month">Ce mois-ci</option>
            <option value="year">Cette année</option>
            <option value="custom">Période personnalisée...</option>
            <option value="all" selected>Tous les clients (Historique complet)</option>
          </select>
        </div>

        <!-- Zone dates personnalisées -->
        <div id="custom-date-container" style="display: none; background: #eff6ff; border: 1px solid #bfdbfe; padding: 12px; border-radius: 8px; margin-top: 10px;">
          <div style="display: flex; gap: 12px;">
            <div style="flex: 1;">
              <label style="font-size: 0.8rem; font-weight: 600; display: block;">Date début :</label>
              <input id="export-start-date" type="date" style="width: 100%; padding: 6px; border-radius: 4px; border: 1px solid #cbd5e1;">
            </div>
            <div style="flex: 1;">
              <label style="font-size: 0.8rem; font-weight: 600; display: block;">Date fin :</label>
              <input id="export-end-date" type="date" style="width: 100%; padding: 6px; border-radius: 4px; border: 1px solid #cbd5e1;">
            </div>
          </div>
        </div>

      </div>
    `;

    Swal.fire({
      title: '📊 Exportation Excel Avancée',
      html: htmlContent,
      icon: null,
      width: '540px',
      showCancelButton: true,
      confirmButtonText: 'Exporter l\'Excel',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#10b981',
      didOpen: () => {
        const select = document.getElementById('export-period-select');
        const customContainer = document.getElementById('custom-date-container');
        const toggleBtn = document.getElementById('toggle-all-etats');

        if (select) {
          select.addEventListener('change', (e) => {
            if (customContainer) {
              customContainer.style.display = e.target.value === 'custom' ? 'block' : 'none';
            }
          });
        }

        let allChecked = true;
        if (toggleBtn) {
          toggleBtn.addEventListener('click', () => {
            allChecked = !allChecked;
            document.querySelectorAll('.export-etat-cb').forEach(cb => {
              cb.checked = allChecked;
            });
          });
        }
      },
      preConfirm: () => {
        const selectedStatuts = Array.from(document.querySelectorAll('.export-statut-cb:checked')).map(cb => cb.value);
        const selectedEtats = Array.from(document.querySelectorAll('.export-etat-cb:checked')).map(cb => cb.value);
        const period = document.getElementById('export-period-select')?.value || 'all';
        const startDate = document.getElementById('export-start-date')?.value;
        const endDate = document.getElementById('export-end-date')?.value;

        if (selectedStatuts.length === 0) {
          Swal.showValidationMessage('Veuillez sélectionner au moins un statut de paiement');
          return false;
        }

        if (selectedEtats.length === 0) {
          Swal.showValidationMessage('Veuillez sélectionner au moins un état de dossier');
          return false;
        }

        if (period === 'custom' && (!startDate || !endDate)) {
          Swal.showValidationMessage('Veuillez spécifier la date de début et de fin');
          return false;
        }

        return {
          selectedStatuts,
          selectedEtats,
          period,
          startDate,
          endDate
        };
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        handleExportExcel(result.value);
      }
    });
  };

  // Exporter les clients vers Excel avec 4 feuilles détaillées (Clients & Dossiers, Paiements, Échéances, Notes & Historique)
  const handleExportExcel = async (exportOptions = {}) => {
    const { selectedStatuts = [], selectedEtats = [], period = 'all', startDate = null, endDate = null } = exportOptions;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = {
        page: 1,
        limit: 10000,
        categorie: filters.categorie,
        district: filters.district,
        typeClient: filters.typeClient,
        search: filters.search
      };

      const now = new Date();
      if (period === 'current') {
        params.mois = filters.mois;
        params.annee = filters.annee;
        if (filters.etatDossier) params.etatDossier = filters.etatDossier;
      } else if (period === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(now.getDate() - 7);
        params.startDate = weekAgo.toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
      } else if (period === 'month') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        params.startDate = startOfMonth.toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
      } else if (period === 'year') {
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        params.startDate = startOfYear.toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
      } else if (period === 'custom') {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      // Récupérer la liste des clients
      const response = await axios.get(getApiUrl('/clients'), {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });

      const allClients = response.data.data || [];

      // Filtrer les clients par statut de paiement sélectionné
      const filteredClients = allClients.filter(client => {
        const clientStatut = client.statutPaiementTotal || 'non_paye';
        if (selectedStatuts.length > 0 && !selectedStatuts.includes(clientStatut)) {
          return false;
        }
        return true;
      });

      if (filteredClients.length === 0) {
        Swal.fire('Info', 'Aucun client ne correspond aux filtres sélectionnés.', 'info');
        return;
      }

      // Préparation des 4 feuilles de données
      const sheetClients = [];
      const sheetPaiements = [];
      const sheetEcheances = [];
      const sheetNotes = [];

      filteredClients.forEach(client => {
        const clientNomComplet = `${client.prenom || ''} ${client.nom || ''}`.trim();

        // Notes globales au niveau du client
        if (Array.isArray(client.notes_array) && client.notes_array.length > 0) {
          client.notes_array.forEach(n => {
            sheetNotes.push({
              'ID CLIENT': client._id || '',
              'CLIENT': clientNomComplet,
              'IDENTIFIANT': (client.cin || client.passeport || ''), 'TYPE ID': (client.cin ? 'CIN' : (client.passeport ? 'Passeport' : '')),
              'CIBLE': 'Client',
              'RÉF STEG': '-',
              'DATE NOTE': n.date ? new Date(n.date).toLocaleString('fr-FR') : '',
              'AUTEUR': n.author || '',
              'PRIORITÉ': n.priorite || 'normale',
              'STATUT NOTE': n.statut || 'ouverte',
              'AFFECTÉ À': n.affecteA ? (typeof n.affecteA === 'object' ? `${n.affecteA.prenom || ''} ${n.affecteA.nom || ''}`.trim() : n.affecteA) : '-',
              'NOTE': n.text || '',
              'RÉPONSES': Array.isArray(n.reponses) ? n.reponses.map(r => `[${r.author || 'User'}] ${r.text}`).join(' | ') : ''
            });
          });
        }

        const activeDossiers = (client.dossiers || []).filter(d => {
          if (d.isDeleted) return false;
          if (selectedEtats.length > 0 && !selectedEtats.includes(d.etatDossier)) {
            return false;
          }
          return true;
        });

        if (activeDossiers.length > 0) {
          activeDossiers.forEach(dossier => {
            const extensionsCount = dossier.extensions?.filter(e => e.statut === 'validee').length || 0;
            const extensionsTotal = dossier.extensions?.filter(e => e.statut === 'validee').reduce((sum, e) => sum + (e.coutExtension || 0), 0) || 0;
            const produitsCount = dossier.produitsClient?.length || 0;
            const produitsTotal = dossier.produitsClient?.reduce((sum, p) => sum + (p.prixTotal || 0), 0) || 0;

            const totalMontantPaiement = Number(dossier.montantTotalPaiement) || 0;
            const comptantPaye = Number(dossier.autofinancement?.montantComptantPaye) || 0;
            const creditPaye = Number(dossier.autofinancement?.montantPaye) || 0;
            const stegPayeMontant = dossier.stegPaye ? (Number(dossier.montantSteg) || 0) : 0;
            const totalPayeDossier = comptantPaye + creditPaye + stegPayeMontant;
            const restantAPayerDossier = Math.max(0, totalMontantPaiement - totalPayeDossier);

            // 1. Feuille Clients & Dossiers
            sheetClients.push({
              'ID CLIENT': client._id || '',
              'NOM CLIENT': client.nom || '',
              'PRÉNOM CLIENT': client.prenom || '',
              'IDENTIFIANT': (client.cin || client.passeport || ''), 'TYPE ID': (client.cin ? 'CIN' : (client.passeport ? 'Passeport' : '')),
              'TÉLÉPHONE': client.telephone || '',
              'ADRESSE': client.adresse || '',
              'TYPE CLIENT': client.typeClient || 'BT',
              'STATUT PAIEMENT GLOBAL': client.statutPaiementTotal === 'paye' ? 'Payé' : client.statutPaiementTotal === 'partiel' ? 'Partiellement payé' : 'Non payé',
              'DATE CRÉATION CLIENT': client.createdAt ? new Date(client.createdAt).toLocaleDateString('fr-FR') : '',

              'ID DOSSIER': dossier._id || '',
              'RÉF STEG': dossier.referenceSteg || '',
              'POMPAGE': dossier.pompage ? 'Oui' : 'Non',
              'PUISSANCE (kWc)': dossier.puissance || 0,
              'DISTRICT': dossier.district?.region || (typeof dossier.district === 'string' ? dossier.district : ''),
              'ID PROJET': dossier.projectId || '',

              'COMMERCIAL': dossier.commercial ? `${dossier.commercial.prenom || ''} ${dossier.commercial.nom || ''}`.trim() : '',
              'TÉL COMMERCIAL': dossier.commercial?.numeroTelephone || '',
              'COMMISSION (%)': dossier.commissionPourcentage ?? '',
              'COMMISSION (DT)': dossier.commissionMontant ?? '',

              'INSTALLATEUR': dossier.installateur?.chefEquipe ? `${dossier.installateur.chefEquipe.prenom || ''} ${dossier.installateur.chefEquipe.nom || ''}`.trim() : '',
              'TÉL INSTALLATEUR': dossier.installateur?.chefEquipe?.numeroTelephone || '',

              'ÉTAT DOSSIER': dossier.etatDossier || '',
              'ÉTAT MODIFIÉ MANUELLEMENT': dossier.modificationManuelleEtat ? 'Oui' : 'Non',
              'MOIS/ANNÉE': dossier.moisAnnee || '',

              'DATE ARRIV. DOSSIER': dossier.dateArrivDossier ? new Date(dossier.dateArrivDossier).toLocaleDateString('fr-FR') : '',
              'DATE DÉPÔT STEG': dossier.dateDepotSteg ? new Date(dossier.dateDepotSteg).toLocaleDateString('fr-FR') : '',
              'DATE APPROBATION B.E.': dossier.dateApprobationBE ? new Date(dossier.dateApprobationBE).toLocaleDateString('fr-FR') : '',
              'DATE DF (PAIEMENT)': dossier.dateDF ? new Date(dossier.dateDF).toLocaleDateString('fr-FR') : '',
              'DATE DEMANDE RÉCEPTION': dossier.dateDemandeReception ? new Date(dossier.dateDemandeReception).toLocaleDateString('fr-FR') : '',
              'DATE LIVRAISON': dossier.dateLivraison ? new Date(dossier.dateLivraison).toLocaleDateString('fr-FR') : '',
              'DATE MISE EN SERVICE': dossier.dateMiseEnService ? new Date(dossier.dateMiseEnService).toLocaleDateString('fr-FR') : '',
              'DATE FACTURE': dossier.dateFacture ? new Date(dossier.dateFacture).toLocaleDateString('fr-FR') : '',

              'TYPE PAIEMENT': dossier.typePaiement || '',
              'MONTANT TOTAL (DT)': totalMontantPaiement,
              'TOTAL PAYÉ (DT)': totalPayeDossier,
              'RESTANT À PAYER (DT)': restantAPayerDossier,

              'MONTANT STEG (DT)': dossier.montantSteg || 0,
              'STEG PAYÉ': dossier.stegPaye ? 'Oui' : 'Non',
              'DATE PAIEMENT STEG': dossier.datePaiementSteg ? new Date(dossier.datePaiementSteg).toLocaleDateString('fr-FR') : '',

              'MONTANT AUTOFINANCEMENT (DT)': dossier.montantAutofinancement || 0,
              'AF COMPTANT DEMANDÉ (DT)': dossier.autofinancement?.montantComptantInitial || 0,
              'AF COMPTANT PAYÉ (DT)': comptantPaye,
              'MODE PAIEMENT COMPTANT': dossier.autofinancement?.modePaiementComptant || '',
              'DATE PAIEMENT COMPTANT': dossier.autofinancement?.datePaiementComptant ? new Date(dossier.autofinancement.datePaiementComptant).toLocaleDateString('fr-FR') : '',

              'AF CRÉDIT TOTAL (DT)': dossier.autofinancement?.montantTotal || 0,
              'AF CRÉDIT PAYÉ (DT)': creditPaye,
              'NB MOIS CRÉDIT': dossier.autofinancement?.nombreMois || 0,
              'MONTANT MENSUEL CRÉDIT (DT)': dossier.autofinancement?.montantMensuel || 0,
              'DATE DÉBUT CRÉDIT': dossier.autofinancement?.dateDebutCredit ? new Date(dossier.autofinancement.dateDebutCredit).toLocaleDateString('fr-FR') : '',
              'BANQUE/CRÉDIT': dossier.credit || '',

              'N° BL': dossier.bl || '',
              'N° FACTURE': dossier.facture || '',
              'NB EXTENSIONS VALIDÉES': extensionsCount,
              'TOTAL EXTENSIONS (DT)': extensionsTotal,
              'NB PRODUITS INSTALLÉS': produitsCount,
              'TOTAL PRIX PRODUITS (DT)': produitsTotal,
              'NOTES DOSSIER': dossier.notes || ''
            });

            // 2. Feuille Paiements
            const paiementsList = dossier.autofinancement?.historiquePaiements || [];
            paiementsList.forEach(p => {
              sheetPaiements.push({
                'ID CLIENT': client._id || '',
                'NOM CLIENT': clientNomComplet,
                'IDENTIFIANT': (client.cin || client.passeport || ''), 'TYPE ID': (client.cin ? 'CIN' : (client.passeport ? 'Passeport' : '')),
                'RÉF STEG': dossier.referenceSteg || '',
                'ID PAIEMENT': p._id || '',
                'DATE PAIEMENT': p.date ? new Date(p.date).toLocaleDateString('fr-FR') : '',
                'MONTANT (DT)': p.montant || 0,
                'CATÉGORIE': p.categorie === 'comptant' ? 'Comptant' : p.categorie === 'credit' ? 'Crédit' : p.categorie === 'steg' ? 'Part STEG' : (p.categorie || '-'),
                'MÉTHODE PAIEMENT': p.methode || 'Espèces',
                'RÉFÉRENCE / TICKET': p.reference || '-',
                'STATUT PAIEMENT': p.statut === 'paye' ? 'Payé' : p.statut === 'en_attente' ? 'En attente' : p.statut === 'annule' ? 'Annulé' : p.statut === 'supprime' ? 'Supprimé' : (p.statut || '-'),
                'NOTES / REMARQUES': p.notes || '-',
                'DATE ENREGISTREMENT': p.createdAt ? new Date(p.createdAt).toLocaleString('fr-FR') : ''
              });
            });

            // 3. Feuille Échéances
            const echeancesList = dossier.autofinancement?.echeancier || [];
            echeancesList.forEach((e, idx) => {
              sheetEcheances.push({
                'ID CLIENT': client._id || '',
                'NOM CLIENT': clientNomComplet,
                'IDENTIFIANT': (client.cin || client.passeport || ''), 'TYPE ID': (client.cin ? 'CIN' : (client.passeport ? 'Passeport' : '')),
                'RÉF STEG': dossier.referenceSteg || '',
                'N° ÉCHÉANCE': idx + 1,
                'DATE ÉCHÉANCE': e.dateEcheance ? new Date(e.dateEcheance).toLocaleDateString('fr-FR') : '',
                'MONTANT MENSUEL (DT)': e.montant || 0,
                'STATUT ÉCHÉANCE': e.statut === 'paye' ? 'Payée' : e.statut === 'retard' ? 'En retard' : e.statut === 'annule' ? 'Annulée' : 'En attente',
                'DATE PAIEMENT EFFECTIF': e.datePaiement ? new Date(e.datePaiement).toLocaleDateString('fr-FR') : '-',
                'NOTES / SUIVI': Array.isArray(e.notes) ? e.notes.join(' | ') : (e.notes || '-')
              });
            });

            // 4. Feuille Notes & Historique
            const dossierNotesList = dossier.notes_array || [];
            dossierNotesList.forEach(n => {
              sheetNotes.push({
                'ID CLIENT': client._id || '',
                'CLIENT': clientNomComplet,
                'IDENTIFIANT': (client.cin || client.passeport || ''), 'TYPE ID': (client.cin ? 'CIN' : (client.passeport ? 'Passeport' : '')),
                'CIBLE': 'Dossier',
                'RÉF STEG': dossier.referenceSteg || '',
                'DATE NOTE': n.date ? new Date(n.date).toLocaleString('fr-FR') : '',
                'AUTEUR': n.author || '',
                'PRIORITÉ': n.priorite || 'normale',
                'STATUT NOTE': n.statut || 'ouverte',
                'AFFECTÉ À': n.affecteA ? (typeof n.affecteA === 'object' ? `${n.affecteA.prenom || ''} ${n.affecteA.nom || ''}`.trim() : n.affecteA) : '-',
                'NOTE': n.text || '',
                'RÉPONSES': Array.isArray(n.reponses) ? n.reponses.map(r => `[${r.author || 'User'}] ${r.text}`).join(' | ') : ''
              });
            });

          });
        } else {
          // Client sans dossier actif retenu
          sheetClients.push({
            'ID CLIENT': client._id || '',
            'NOM CLIENT': client.nom || '',
            'PRÉNOM CLIENT': client.prenom || '',
            'IDENTIFIANT': (client.cin || client.passeport || ''), 'TYPE ID': (client.cin ? 'CIN' : (client.passeport ? 'Passeport' : '')),
            'TÉLÉPHONE': client.telephone || '',
            'ADRESSE': client.adresse || '',
            'TYPE CLIENT': client.typeClient || 'BT',
            'STATUT PAIEMENT GLOBAL': client.statutPaiementTotal === 'paye' ? 'Payé' : client.statutPaiementTotal === 'partiel' ? 'Partiellement payé' : 'Non payé',
            'DATE CRÉATION CLIENT': client.createdAt ? new Date(client.createdAt).toLocaleDateString('fr-FR') : '',
            'RÉF STEG': 'Aucun dossier actif retenu'
          });
        }
      });

      // ── Création du classeur Excel avec les feuilles formatées ──
      const workbook = XLSX.utils.book_new();

      // Helper pour styliser chaque feuille (largeurs automatiques, ligne d'en-tête figée, filtres automatiques Excel)
      const styleWorksheet = (ws, dataArray) => {
        if (!dataArray || dataArray.length === 0) return;

        // 1. Largeur automatique des colonnes
        const columnWidths = Object.keys(dataArray[0] || {}).map(key => {
          let maxLen = key.length;
          dataArray.forEach(row => {
            const valStr = row[key] !== null && row[key] !== undefined ? String(row[key]) : '';
            if (valStr.length > maxLen) maxLen = valStr.length;
          });
          return { wch: Math.min(Math.max(maxLen + 3, 14), 55) };
        });
        ws['!cols'] = columnWidths;

        // 2. Figer la 1ère ligne (En-têtes) pour le défilement
        ws['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: 1, activePane: 'bottomLeft', topLeftCell: 'A2' }];

        // 3. Activer les filtres automatiques Excel sur toute la plage
        if (ws['!ref']) {
          ws['!autofilter'] = { ref: ws['!ref'] };
        }
      };

      // ── Feuille 1 : Tous les Clients et Dossiers ──
      const wsClients = XLSX.utils.json_to_sheet(sheetClients.length > 0 ? sheetClients : [{'MESSAGE': 'Aucun client/dossier correspondant'}]);
      styleWorksheet(wsClients, sheetClients);
      XLSX.utils.book_append_sheet(workbook, wsClients, 'Clients & Dossiers (Tous)');

      // ── Feuille 1.A : Clients Payés ──
      const sheetPayes = sheetClients.filter(r => r['STATUT PAIEMENT GLOBAL'] === 'Payé');
      if (sheetPayes.length > 0) {
        const wsPayes = XLSX.utils.json_to_sheet(sheetPayes);
        styleWorksheet(wsPayes, sheetPayes);
        XLSX.utils.book_append_sheet(workbook, wsPayes, 'Clients Payés');
      }

      // ── Feuille 1.B : Clients Partiellement Payés ──
      const sheetPartiels = sheetClients.filter(r => r['STATUT PAIEMENT GLOBAL'] === 'Partiellement payé');
      if (sheetPartiels.length > 0) {
        const wsPartiels = XLSX.utils.json_to_sheet(sheetPartiels);
        styleWorksheet(wsPartiels, sheetPartiels);
        XLSX.utils.book_append_sheet(workbook, wsPartiels, 'Clients Partiels');
      }

      // ── Feuille 1.C : Clients Non Payés ──
      const sheetNonPayes = sheetClients.filter(r => r['STATUT PAIEMENT GLOBAL'] === 'Non payé');
      if (sheetNonPayes.length > 0) {
        const wsNonPayes = XLSX.utils.json_to_sheet(sheetNonPayes);
        styleWorksheet(wsNonPayes, sheetNonPayes);
        XLSX.utils.book_append_sheet(workbook, wsNonPayes, 'Clients Non Payés');
      }

      // ── Feuille 2 : Paiements ──
      const wsPaiements = XLSX.utils.json_to_sheet(sheetPaiements.length > 0 ? sheetPaiements : [{'MESSAGE': 'Aucun paiement trouvé'}]);
      styleWorksheet(wsPaiements, sheetPaiements);
      XLSX.utils.book_append_sheet(workbook, wsPaiements, 'Paiements');

      // ── Feuille 3 : Échéances ──
      const wsEcheances = XLSX.utils.json_to_sheet(sheetEcheances.length > 0 ? sheetEcheances : [{'MESSAGE': 'Aucune échéance trouvée'}]);
      styleWorksheet(wsEcheances, sheetEcheances);
      XLSX.utils.book_append_sheet(workbook, wsEcheances, 'Échéances');

      // ── Feuille 4 : Notes & Historique ──
      const wsNotes = XLSX.utils.json_to_sheet(sheetNotes.length > 0 ? sheetNotes : [{'MESSAGE': 'Aucune note enregistrée'}]);
      styleWorksheet(wsNotes, sheetNotes);
      XLSX.utils.book_append_sheet(workbook, wsNotes, 'Notes & Historique');

      // Télécharger le fichier Excel
      const dateStr = now.toISOString().split('T')[0];
      XLSX.writeFile(workbook, `export-clients-sastra-${dateStr}.xlsx`);

      Swal.fire({
        title: 'Exportation Réussie !',
        html: `<strong>${filteredClients.length}</strong> client(s) exporté(s) dans le classeur Excel :<br><br>
               • <strong>${sheetClients.length}</strong> dans <em>Clients & Dossiers (Tous)</em><br>
               ${sheetPayes.length > 0 ? `• <strong>${sheetPayes.length}</strong> dans <em>Clients Payés</em><br>` : ''}
               ${sheetPartiels.length > 0 ? `• <strong>${sheetPartiels.length}</strong> dans <em>Clients Partiels</em><br>` : ''}
               ${sheetNonPayes.length > 0 ? `• <strong>${sheetNonPayes.length}</strong> dans <em>Clients Non Payés</em><br>` : ''}
               • <strong>${sheetPaiements.length}</strong> dans <em>Paiements</em><br>
               • <strong>${sheetEcheances.length}</strong> dans <em>Échéances</em><br>
               • <strong>${sheetNotes.length}</strong> dans <em>Notes & Historique</em>`,
        icon: 'success',
        confirmButtonColor: '#10b981'
      });

    } catch (err) {
      console.error('Erreur lors de l\'exportation Excel:', err);
      setError('Erreur lors de l\'exportation des clients');
      setTimeout(clearMessages, 4000);
    } finally {
      setLoading(false);
    }
  };

  // Gestionnaire de formulaire
  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFieldErrors(prev => ({
      ...prev,
      [name]: null,
      ...(name === 'typeIdentifiant' ? { cin: null, passeport: null } : {})
    }));
    setFormData(prev => {
      let nextValue = value;
      if (name === 'cin') {
        // CIN : uniquement des chiffres, max 8
        nextValue = value.replace(/\D/g, '').slice(0, 8);
      } else if (name === 'telephone') {
        // Téléphone : uniquement des chiffres, max 8
        nextValue = value.replace(/\D/g, '').slice(0, 8);
      } else if (name === 'typeIdentifiant') {
        // Changer de type d'identifiant : vider l'autre champ
        return { ...prev, typeIdentifiant: value, cin: '', passeport: '' };
      } else {
        const numericFields = new Set(['puissance', 'montantTotalPaiement', 'montantComptant']);
        nextValue = numericFields.has(name) ? normalizeDecimalInput(value) : value;
      }
      const updated = {...prev, [name]: nextValue};
      
      // Automatisation : le type de client est déterminé par la longueur de la référence STEG
      if (name === 'referenceSteg') {
        const cleanRef = value.replace(/\s/g, '');
        if (cleanRef.length === 6) {
          updated.typeClient = 'MT';
          updated.cin = '';
          updated.passeport = '';
        } else if (cleanRef.length === 9) {
          updated.typeClient = 'BT';
        }
      }

      if (name === 'typeClient' && value === 'MT') {
        updated.cin = '';
        updated.passeport = '';
      }

      // Extraction automatique du mois et de l'année depuis la date d'arrivée dossier
      if (name === 'dateArrivDossier') {
        const dDate = new Date(value);
        if (!isNaN(dDate.getTime())) {
          const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
          updated.moisAnnee = `${MOIS[dDate.getMonth()]} ${dDate.getFullYear()}`;
        }
      }

      if (name === 'montantTotalPaiement') {
        const total = Math.max(0, Number(nextValue) || 0);
        if (total > 10000 && updated.typePaiement === 'credit') {
          updated.typePaiement = 'mixte';
        }
        if (total <= 10000 && updated.typePaiement === 'mixte') {
          updated.typePaiement = 'credit';
        }
      }
      return updated;
    });
  };

  const handleCategorieStegChange = (e) => {
    const categorie = e.target.value;
    setFormData(prev => ({...prev, _categorieSteg: categorie, district: ''}));
    setStegsByCategorie(categorie ? allStegs.filter(s => s.categorie === categorie) : []);
    setLoadingSteg(false);
  };

  // Gestionnaire de filtre
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters(prev => {
      const next = { ...prev, [name]: value };

      // Quand la catégorie change (ou est vidée), réinitialiser le district (région)
      if (name === 'categorie') {
        next.district = '';
      }

      return next;
    });

    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const calculerRepartitionPaiement = (data) => {
    const total = Math.max(0, Number(data.montantTotalPaiement) || 0);
    const nbMois = Math.max(0, parseInt(data.nombreMoisCredit, 10) || 0);
    const acompteSaisi = data.acompteVerse ? Math.max(0, Number(data.montantComptant) || 0) : 0;

    let montantSteg = 0;
    if (data.montantSteg !== undefined && data.montantSteg !== null && data.montantSteg !== '' && !isNaN(Number(data.montantSteg))) {
      montantSteg = Math.min(Math.max(0, Number(data.montantSteg)), total);
    }

    const montantAutofinancement = Number(Math.max(0, total - montantSteg).toFixed(2));

    // Déterminer le type effectif pour l'affichage / soumission
    let typeEffectif = 'comptant';
    if (montantSteg === 0) {
      typeEffectif = 'comptant';
    } else if (total > 0 && Math.abs(montantSteg - total) <= 0.01) {
      typeEffectif = 'credit';
    } else if (montantSteg > 0 && montantSteg < total) {
      typeEffectif = 'mixte';
    }

    // Répartir l'autofinancement entre Comptant (Acompte) et Crédit (Échéances)
    let finalComptant = 0;
    let finalCredit = 0;

    if (nbMois > 0) {
      finalComptant = Math.min(montantAutofinancement, acompteSaisi);
      finalCredit = Math.max(0, Number((montantAutofinancement - finalComptant).toFixed(2)));
    } else {
      finalComptant = montantAutofinancement;
      finalCredit = 0;
    }

    return {
      typePaiement: typeEffectif,
      montantTotalPaiement: Number(total.toFixed(2)),
      montantSteg: Number(montantSteg.toFixed(2)),
      montantAutofinancement: Number(montantAutofinancement.toFixed(2)),
      montantComptant: Number(finalComptant.toFixed(2)),
      montantCredit: Number(finalCredit.toFixed(2))
    };
  };

  const validerScenarioPuissanceMontant = (puissanceValue, montantTotalValue) => {
    return null;
  };

  // Validation du formulaire d'ajout / édition client
  const validateClientForm = (data, isEdit = false) => {
    const errors = [];

    // --- Champs client ---
    if (!data.typeClient) errors.push('❌ Type de client requis (BT ou MT).');
    if (!data.nom || data.nom.trim() === '') errors.push('❌ Le nom du client est obligatoire.');
    if (data.typeClient === 'BT' && (!data.prenom || data.prenom.trim() === ''))
      errors.push('❌ Le prénom est obligatoire pour un client de type BT.');

    // --- Identifiant XOR (CIN ou Passeport ; Optionnel pour MT) ---
    if (data.typeClient !== 'MT') {
      const hasCin = data.typeIdentifiant === 'cin' && data.cin && data.cin.trim() !== '';
      const hasPasseport = data.typeIdentifiant === 'passeport' && data.passeport && data.passeport.trim() !== '';

      if (!hasCin && !hasPasseport) {
        errors.push('❌ Veuillez renseigner un CIN ou un numéro de passeport.');
      } else if (hasCin && !/^\d{8}$/.test(data.cin.trim())) {
        errors.push('❌ Le CIN doit contenir exactement 8 chiffres.');
      } else if (hasPasseport && data.passeport.trim().length < 3) {
        errors.push('❌ Le numéro de passeport doit contenir au moins 3 caractères.');
      }
    }

    if (data.telephone && data.telephone.trim() !== '') {
      if (!/^\d{8}$/.test(data.telephone.trim())) {
        errors.push('❌ Le numéro de téléphone doit contenir exactement 8 chiffres.');
      }
    }

    if (data.email && !/^\S+@\S+\.\S+$/.test(data.email))
      errors.push('❌ L\'adresse email est invalide (ex: nom@domaine.com).');

    return errors;
  };

  const validateDossierForm = (data, options = {}) => {
    const {requireFinancial = false} = options;
    const errors = [];
    if (!data.pompage) {
      if (!data.referenceSteg || data.referenceSteg.trim() === '') {
        errors.push('❌ La référence STEG est obligatoire.');
      } else {
        const refStr = data.referenceSteg.trim();
        if (data.typeClient === 'BT' && !/^\d{9}$/.test(refStr)) {
          errors.push('❌ La référence STEG pour un client BT doit comporter exactement 9 chiffres.');
        } else if (data.typeClient === 'MT' && !/^\d{6}$/.test(refStr)) {
          errors.push('❌ La référence STEG pour un client MT doit comporter exactement 6 chiffres.');
        }
      }
    }
    if (data.puissance !== '' && Number(data.puissance) < 0)
      errors.push('❌ La puissance ne peut pas être négative.');
    if (data.montantTotalPaiement !== '' && Number(data.montantTotalPaiement) < 0)
      errors.push('❌ Le montant total ne peut pas être négatif.');

    const montantTotal = Number(data.montantTotalPaiement) || 0;
    if (requireFinancial && montantTotal <= 0) {
      errors.push('❌ Le montant total de paiement doit être supérieur à 0.');
    }

    const messageScenario = validerScenarioPuissanceMontant(data.puissance, montantTotal);
    if (messageScenario) {
      errors.push(messageScenario);
    }

    return errors;
  };

  // Parse les erreurs retournées par le backend
  const parseBackendError = (err) => {
    const status = err.response?.status;
    const data = err.response?.data;

    if (status === 409) {
      if (data?.message) return `❌ ${data.message}`;
      return '❌ Un conflit d\'unicité a été détecté (CIN ou référence STEG déjà existante).';
    }
    if (status === 401) return '❌ Session expirée. Veuillez vous reconnecter.';
    if (status === 403) return '❌ Vous n\'avez pas la permission d\'effectuer cette action.';
    if (status === 404) return '❌ Client introuvable. Il a peut-être été supprimé entre-temps.';
    if (status === 400) {
      if (data?.errors) {
        const fieldErrors = Object.values(data.errors).map(e => `❌ ${e.message}`);
        return fieldErrors.join('\n');
      }
      if (data?.message) return `❌ ${data.message}`;
    }
    if (status >= 500) return '❌ Erreur serveur. Veuillez réessayer dans quelques instants.';
    return data?.message || '❌ Une erreur inattendue est survenue.';
  };

  // Soumission du formulaire d'ajout
  const handleAddSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSubmittingClient) return;
    clearMessages();

    // Empêche la soumission prématurée (ex: touche Entrée aux étapes 1/2/3)
    if (currentStep < 4) {
      nextStep();
      return;
    }

    // 1. Validation frontend
    const clientVal = validateClientFormUtil(formData, { isEdit: false });
    const dossierVal = validateDossierFormUtil(formData, { typeClient: formData.typeClient, requireFinancial: true });
    const combinedErrors = { ...(clientVal.errors || {}), ...(dossierVal.errors || {}) };

    if (fieldErrors?.cin) combinedErrors.cin = fieldErrors.cin;
    if (fieldErrors?.referenceSteg) combinedErrors.referenceSteg = fieldErrors.referenceSteg;

    if (!clientVal.isValid || !dossierVal.isValid || fieldErrors?.cin || fieldErrors?.referenceSteg) {
      setFieldErrors(combinedErrors);
      setError(Object.values(combinedErrors).filter(Boolean).join('\n'));
      return;
    }

    try {
      setIsSubmittingClient(true);
      const token = localStorage.getItem('token');
      if (!token) { setError('Session expirée. Veuillez vous reconnecter.'); return; }

      // Construire l'objet client avec le premier dossier
      const id = (v) => (v && v.trim() !== '' ? v : null);
      const repartition = calculerRepartitionPaiement(formData);
      const isAcompteValide = !!formData.acompteVerse && (parseFloat(formData.montantComptant) > 0);
      const acompteMontant = isAcompteValide ? Math.max(0, parseFloat(formData.montantComptant) || 0) : 0;
      const modePaiementComptant = isAcompteValide ? (formData.modePaiementComptant || 'especes') : 'especes';
      const datePaiementComptant = isAcompteValide ? (formData.datePaiementComptant || formData.dateArrivDossier || new Date().toISOString().split('T')[0]) : null;

      const creditAmount = Math.max(0, repartition.montantAutofinancement - acompteMontant);
      const nombreMoisCreditVal = creditAmount > 0 ? Math.max(0, parseInt(formData.nombreMoisCredit, 10) || 0) : 0;
      const montantMensuelCalc = (creditAmount > 0 && nombreMoisCreditVal > 0) ? Math.round((creditAmount / nombreMoisCreditVal) * 100) / 100 : 0;

      const clientData = {
        // Informations client
        nom: formData.nom,
        prenom: formData.prenom,
        cin: formData.typeIdentifiant === 'cin' ? formData.cin : null,
        passeport: formData.typeIdentifiant === 'passeport' ? formData.passeport : null,
        telephone: formData.telephone,
        email: formData.email,
        adresse: formData.adresse,
        typeClient: formData.typeClient,
        packId: id(formData.packId),
        // Ajouter le premier dossier
        dossiers: [{
          packId: id(formData.packId),
          pompage: formData.pompage,
          referenceSteg: formData.referenceSteg,
          puissance: parseFloat(formData.puissance) || 0,
          district: id(formData.district),
          moisAnnee: formData.moisAnnee,
          typePaiement: repartition.typePaiement,
          montantTotalPaiement: repartition.montantTotalPaiement,
          montantSteg: repartition.montantSteg,
          montantAutofinancement: repartition.montantAutofinancement,
          montantComptant: acompteMontant,
          montantCredit: creditAmount,
          nombreMoisCredit: nombreMoisCreditVal,
          dateDebutCredit: nombreMoisCreditVal > 0 ? (formData.dateDebutCredit || null) : null,
          modePaiementComptant: isAcompteValide ? modePaiementComptant : 'especes',
          stegPaye: false,
          datePaiementSteg: null,
          autofinancement: {
            montantTotal: creditAmount,
            montantPaye: 0,
            nombreMois: nombreMoisCreditVal,
            montantMensuel: montantMensuelCalc,
            dateDebutCredit: nombreMoisCreditVal > 0 ? (formData.dateDebutCredit || null) : null,
            montantComptantInitial: acompteMontant,
            montantComptantPaye: acompteMontant,
            modePaiementComptant: isAcompteValide ? modePaiementComptant : 'especes',
            datePaiementComptant: isAcompteValide ? datePaiementComptant : null,
            historiquePaiements: isAcompteValide ? [{
              categorie: 'comptant',
              typePaiementDetail: 'avance',
              montant: acompteMontant,
              datePaiement: datePaiementComptant,
              modePaiement: modePaiementComptant,
              statut: 'paye',
              notes: 'Acompte versé à la création du dossier'
            }] : [],
            echeancier: [],
            prochaineEcheance: { montant: 0, dateEcheance: null }
          },
          dateArrivDossier: formData.dateArrivDossier || new Date().toISOString().split('T')[0],
          dateDepotSteg: formData.dateDepotSteg || null,
          dateDemandeReception: formData.dateDemandeReception || null,
          dateLivraison: formData.dateLivraison || null,
          dateMiseEnService: formData.dateMiseEnService || null,
          dateDF: formData.dateDF || null,
          commercial: id(formData.commercial),
          installateur: id(formData.installateur),
          etatDossier: formData.etatDossier || 'Déposé chez Sastra',
          bl: formData.bl,
          facture: formData.facture,
          dateFacture: formData.dateFacture || null,
          notes: formData.notes
        }]
      };

      await axios.post(getApiUrl('/clients'), clientData, {
        headers: authH()
      });
      setShowAddForm(false);
      setCurrentStep(1);
      fetchClients();
      setMessage('✅ Client ajouté avec succès !');
    } catch (err) {
      console.error('Erreur lors de l\'ajout:', err);
      
      // Gestion des doublons CIN (Cas client supprimé)
      if (err.response?.status === 409 && err.response?.data?.isDeleted) {
        const { clientId, clientName } = err.response.data;
        const token = localStorage.getItem('token');

        const result = await Swal.fire({
          title: 'Client supprimé trouvé',
          html: `Le CIN <b>${formData.cin}</b> appartient à un client supprimé (<b>${clientName}</b>).<br/><br/>Que souhaitez-vous faire ?`,
          icon: 'warning',
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: 'Restaurer ce client',
          denyButtonText: 'Créer un nouveau client',
          cancelButtonText: 'Annuler',
          confirmButtonColor: '#3085d6',
          denyButtonColor: '#10b981',
          cancelButtonColor: '#94a3b8'
        });

        if (result.isConfirmed) {
          try {
            setLoading(true);
            await axios.put(getApiUrl(`/clients/${clientId}/restore`), {}, {
              headers: authH()
            });
            fetchClients();
            setShowAddForm(false);
            setCurrentStep(1);
            Swal.fire('Restauré !', 'Le client a été restauré avec succès.', 'success');
          } catch (restoreErr) {
            setError(extractBackendError(restoreErr));
            setFieldErrors(mapBackendErrorToField(restoreErr));
          } finally {
            setLoading(false);
          }
        } else if (result.isDenied) {
          try {
            setLoading(true);
            await axios.post(getApiUrl('/clients'), { ...clientData, forceCreate: true }, {
              headers: authH()
            });
            fetchClients();
            setShowAddForm(false);
            setCurrentStep(1);
            Swal.fire('Créé !', 'Un nouveau client indépendant a été créé.', 'success');
          } catch (createErr) {
            setError(extractBackendError(createErr));
            setFieldErrors(mapBackendErrorToField(createErr));
          } finally {
            setLoading(false);
          }
        }
        return;
      }

      setError(extractBackendError(err));
      setFieldErrors(mapBackendErrorToField(err));
    }
  };

  // Soumission du formulaire d'édition
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    // 1. Validation frontend
    const clientVal = validateClientFormUtil(formData, { isEdit: true });
    const combinedErrors = { ...(clientVal.errors || {}) };
    if (fieldErrors?.cin) combinedErrors.cin = fieldErrors.cin;

    if (!clientVal.isValid || fieldErrors?.cin) {
      setFieldErrors(combinedErrors);
      setError(Object.values(combinedErrors).filter(Boolean).join('\n'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) { setError('Session expirée. Veuillez vous reconnecter.'); return; }

      const clientData = {
        nom: formData.nom,
        prenom: formData.prenom,
        cin: formData.typeIdentifiant === 'cin' ? formData.cin : null,
        passeport: formData.typeIdentifiant === 'passeport' ? formData.passeport : null,
        telephone: formData.telephone,
        email: formData.email,
        adresse: formData.adresse,
        typeClient: formData.typeClient
      };

      await axios.put(getApiUrl(`/clients/${selectedClient._id}`), clientData, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowEditModal(false);
      setSelectedClient(null);
      fetchClients();
      setMessage('✅ Client modifié avec succès !');
    } catch (err) {
      console.error('Erreur lors de la modification:', err);
      setError(parseBackendError(err));
    }
  };

  // Confirmation de suppression
  const handleDeleteConfirm = async () => {
    const { value: confirmText } = await Swal.fire({
      title: 'Action Irréversible !',
      html: `
        <div style="text-align: left; background: #fff5f5; padding: 15px; border-left: 4px solid #ef4444; border-radius: 4px; margin-bottom: 15px;">
          <p style="color: #991b1b; font-weight: 600; margin-bottom: 8px;">⚠️ ATTENTION : Suppression Client</p>
          <p style="color: #b91c1c; font-size: 0.9rem;">Cette action va :</p>
          <ul style="color: #b91c1c; font-size: 0.85rem; margin-top: 5px;">
            <li>Supprimer tous les dossiers associés</li>
            <li>Remettre tous les produits installés en stock</li>
            <li><b>Convertir automatiquement ce client en Prospect</b></li>
            <li>Conserver un audit complet de ses données</li>
          </ul>
        </div>
        <p style="font-weight: 600;">Veuillez taper <b style="color: #ef4444;">SUPPRIMER</b> pour confirmer :</p>
      `,
      input: 'text',
      inputPlaceholder: 'Tapez SUPPRIMER ici...',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Confirmer la suppression',
      cancelButtonText: 'Annuler',
      inputValidator: (value) => {
        if (value !== 'SUPPRIMER') {
          return 'Vous devez taper SUPPRIMER en majuscules pour confirmer';
        }
      }
    });

    if (!confirmText) return;

    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(getApiUrl(`/clients/${selectedClient._id}`), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowDeleteModal(false);
      setSelectedClient(null);
      fetchClients();
      
      Swal.fire({
        icon: 'success',
        title: 'Client converti en prospect',
        text: 'Le client a été supprimé et ses données archivées dans la liste des prospects.',
        timer: 2000
      });
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(err.response?.status === 404 ? 'Client non trouvé.' : 'Erreur lors de la suppression du client');
    }
  };

  // Import Excel
  const handleFileChange = (e) => {
    setExcelFile(e.target.files[0]);
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      setError('Veuillez sélectionner un fichier Excel.');
      return;
    }

    clearMessages();
    const formData = new FormData();
    formData.append('file', excelFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(getApiUrl('/clients/import'), formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setImportResult(response.data);
      fetchClients();
      const totalImportes = response.data.data?.total?.success || 0;
      const totalErrors = response.data.data?.total?.errors || 0;
      setMessage(`Import réussi : ${totalImportes} clients importés${totalErrors > 0 ? `, ${totalErrors} erreurs` : ''}`);
    } catch (err) {
      console.error('Erreur lors de l\'import:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'import du fichier Excel');
    }
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowAddModal(false);
    setShowImportModal(false);
    setCurrentStep(1);
    setSelectedClient(null);
    setExcelFile(null);
    setImportResult(null);
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      adresse: '',
      cin: '',
      passeport: '',
      typeIdentifiant: 'cin',
      typeClient: 'BT',
      referenceSteg: '',
      puissance: '',
      district: '',
      moisAnnee: '',
      typePaiement: 'comptant',
      montantTotalPaiement: 0,
      montantComptant: 0,
      montantCredit: 0,
      nombreMoisCredit: 0,
      dateDebutCredit: new Date().toISOString().split('T')[0],
      modePaiementComptant: 'especes',
      dateArrivDossier: '',
      dateDepotSteg: '',
      dateDemandeReception: '',
      dateLivraison: '',
      dateMiseEnService: '',
      dateDF: '',
      commercial: '',
      installateur: '',
      etatDossier: 'Déposé chez Sastra',
      bl: '',
      facture: '',
      dateFacture: '',
      notes: '',
      packId: ''
    });
  };

  // Les clients sont déjà filtrés par le backend
  const filteredClients = Array.isArray(clients) ? clients : [];
  const repartitionPreview = calculerRepartitionPaiement(formData);

  return (
    <div className="clients-management-container">
      <BackOfficeMenu
        onNavigate={onNavigate}
        onLogout={onLogout}
        currentPage={currentPage}
      />
      <div className="clients-management-content">
        <div className="dashboard-header">
          <div>
            <h1>Gestion des Clients</h1>
            <p>Administration et création des dossiers clients SASTRA Energy</p>
          </div>
          <div className="header-actions">
            <button className="btn-export" onClick={showExportOptions}>📥 Exporter Excel</button>
            {hasPermission('clients', 'create') && (
              <>
                <button 
                  className="btn-import" 
                  onClick={handleImport} 
                  disabled 
                  style={{opacity: 0.6, cursor: 'not-allowed'}} 
                  title="L'importation est temporairement désactivée"
                >
                  📂 Importer Excel
                </button>
                <button className="btn-add-client" onClick={handleAdd}>
                  {showAddForm ? '❌ Masquer le formulaire' : '➕ Ajouter un client'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Formulaire d'ajout directement intégré dans la page lors du clic */}
        {hasPermission('clients', 'create') && showAddForm && (
          <div className="add-client-inline-card">
            <div className="inline-card-header">
              <div className="header-title-group">
                <div className="header-title-badge">NOUVEAU CLIENT</div>
                <div>
                  <h2>Création Client & Dossier</h2>
                  <p>Saisissez les informations nécessaires pour enregistrer un nouveau dossier client</p>
                </div>
              </div>
              <button type="button" className="btn-reset-form-inline" onClick={handleCancelAdd} title="Réinitialiser le formulaire">
                Réinitialiser
              </button>
            </div>

            <div className="form-container-v3">
              {/* Stepper moderne */}
              <div className="stepper-horizontal">
                <div className={`step-item ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                  <div className="step-circle">1</div>
                  <div className="step-label">Client & Identité</div>
                </div>
                <div className="step-line"></div>
                <div className={`step-item ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                  <div className="step-circle">2</div>
                  <div className="step-label">Dossier & STEG</div>
                </div>
                <div className="step-line"></div>
                <div className={`step-item ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
                  <div className="step-circle">3</div>
                  <div className="step-label">Finance & Règlements</div>
                </div>
                <div className="step-line"></div>
                <div className={`step-item ${currentStep >= 4 ? 'active' : ''}`}>
                  <div className="step-circle">4</div>
                  <div className="step-label">Vérification & Confirmation</div>
                </div>
              </div>

              {/* Messages d'erreur ou succès */}
              {error && !showEditModal && !showImportModal && (
                <div className="error-message" style={{ marginBottom: '15px', whiteSpace: 'pre-line' }}>
                  {error}
                </div>
              )}
              {message && !showEditModal && !showImportModal && (
                <div className="success-message" style={{ marginBottom: '15px' }}>
                  {message}
                </div>
              )}

              <form onSubmit={(e) => e.preventDefault()} className="direct-add-form">
                {/* Étape 1 : Informations Client & Identité */}
                {currentStep === 1 && (
                  <div className="fade-in step-one-compact">
                    <div className="form-section-header">
                      <h3 className="form-section-title">1. Informations Client & Identité</h3>
                      <p className="form-section-desc">Sélectionnez le type de client et renseignez les coordonnées principales</p>
                    </div>

                    <div className="form-grid-2col">
                      {/* Choix Type Client (Boutons segmentés) */}
                      <div className="form-group form-grid-full">
                        <label>Type de client <span className="required-star">*</span></label>
                        <div className="segmented-control">
                          <button
                            type="button"
                            className={`segmented-btn ${formData.typeClient === 'BT' ? 'active' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'typeClient', value: 'BT' } })}
                          >
                            BT · Basse Tension (Particulier)
                          </button>
                          <button
                            type="button"
                            className={`segmented-btn ${formData.typeClient === 'MT' ? 'active' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'typeClient', value: 'MT' } })}
                          >
                            MT · Moyenne Tension (Professionnel)
                          </button>
                          <button
                            type="button"
                            className={`segmented-btn ${formData.typeClient === 'BT/MT' ? 'active' : ''}`}
                            onClick={() => handleInputChange({ target: { name: 'typeClient', value: 'BT/MT' } })}
                          >
                            BT / MT · Mixte
                          </button>
                        </div>
                      </div>

                      {/* Choix Identifiant CIN / Passeport (Boutons segmentés - Masqué pour MT) */}
                      {formData.typeClient !== 'MT' && (
                        <div className="form-group form-grid-full">
                          <label>Type d'identifiant <span className="required-star">*</span></label>
                          <div className="segmented-control" style={{ marginBottom: '10px' }}>
                            <button
                              type="button"
                              className={`segmented-btn ${formData.typeIdentifiant === 'cin' ? 'active emerald' : ''}`}
                              onClick={() => handleInputChange({ target: { name: 'typeIdentifiant', value: 'cin' } })}
                            >
                              Carte CIN (8 chiffres)
                            </button>
                            <button
                              type="button"
                              className={`segmented-btn ${formData.typeIdentifiant === 'passeport' ? 'active emerald' : ''}`}
                              onClick={() => handleInputChange({ target: { name: 'typeIdentifiant', value: 'passeport' } })}
                            >
                              Passeport
                            </button>
                          </div>

                          {formData.typeIdentifiant === 'cin' && (
                            <>
                              <input
                                type="text"
                                name="cin"
                                value={formData.cin}
                                onChange={handleInputChange}
                                placeholder="Numéro de CIN (ex: 08765432)"
                                inputMode="numeric"
                                maxLength={8}
                                required
                                className={fieldErrors?.cin ? 'input-has-error' : ''}
                              />
                              {fieldErrors?.cin && <span className="field-error-text">{fieldErrors.cin}</span>}
                            </>
                          )}

                          {formData.typeIdentifiant === 'passeport' && (
                            <>
                              <input
                                type="text"
                                name="passeport"
                                value={formData.passeport}
                                onChange={handleInputChange}
                                placeholder="Numéro de passeport (ex: A12345678)"
                                maxLength={30}
                                required
                                className={fieldErrors?.passeport ? 'input-has-error' : ''}
                              />
                              {fieldErrors?.passeport && <span className="field-error-text">{fieldErrors.passeport}</span>}
                            </>
                          )}
                        </div>
                      )}

                      {/* Champs Nom et Prénom */}
                      <div className="form-group">
                        <label>Nom {formData.typeClient === 'MT' ? '(Raison sociale)' : ''} <span className="required-star">*</span></label>
                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleInputChange}
                          required
                          placeholder={formData.typeClient === 'MT' ? 'Raison sociale entreprise' : 'Nom de famille'}
                          className={fieldErrors?.nom ? 'input-has-error' : ''}
                        />
                        {fieldErrors?.nom && <span className="field-error-text">{fieldErrors.nom}</span>}
                      </div>

                      <div className="form-group">
                        <label>Prénom {formData.typeClient === 'MT' ? '(optionnel)' : ''} {formData.typeClient === 'BT' && <span className="required-star">*</span>}</label>
                        <input
                          type="text"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleInputChange}
                          required={formData.typeClient === 'BT'}
                          placeholder="Prénom"
                          className={fieldErrors?.prenom ? 'input-has-error' : ''}
                        />
                        {fieldErrors?.prenom && <span className="field-error-text">{fieldErrors.prenom}</span>}
                      </div>

                      {/* Coordonnées : Téléphone & Email */}
                      <div className="form-group">
                        <label>Téléphone <span className="required-star">*</span></label>
                        <input
                          type="text"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleInputChange}
                          placeholder="Numéro à 8 chiffres (ex: 20123456)"
                          inputMode="numeric"
                          maxLength={8}
                          required
                          className={fieldErrors?.telephone ? 'input-has-error' : ''}
                        />
                        {fieldErrors?.telephone && <span className="field-error-text">{fieldErrors.telephone}</span>}
                      </div>

                      <div className="form-group">
                        <label>Adresse Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="adresse@domaine.com"
                          className={fieldErrors?.email ? 'input-has-error' : ''}
                        />
                        {fieldErrors?.email && <span className="field-error-text">{fieldErrors.email}</span>}
                      </div>

                      {/* Adresse physique */}
                      <div className="form-group form-grid-full">
                        <label>Adresse complète</label>
                        <input
                          type="text"
                          name="adresse"
                          value={formData.adresse}
                          onChange={handleInputChange}
                          placeholder="Adresse complète (ex: Rue de la liberté, Tunis)"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Étape 2 : Détails du Dossier & STEG */}
                {currentStep === 2 && (
                  <div className="fade-in step-two-compact">
                    <div className="form-section-header">
                      <h3 className="form-section-title">2. Détails du Dossier & STEG</h3>
                      <p className="form-section-desc">Associez un Pack Sastra ou définissez les paramètres techniques du projet</p>
                    </div>

                    <div className="form-grid-2col">
                      {/* Sélection de Pack */}
                      <div className="form-group form-grid-full">
                        <label>Pack Sastra Energy (Remplissage automatique)</label>
                        <select
                          name="packId"
                          value={formData.packId || ''}
                          onChange={handlePackSelectChange}
                          className="notranslate"
                          translate="no"
                        >
                          <option value="">— Aucun pack (Mode Saisie Manuelle) —</option>
                          {packsList.map(p => {
                            const powerStr = p.puissance ? (String(p.puissance).toLowerCase().includes('kwc') ? p.puissance : `${p.puissance} kWc`) : 'Puissance N/A';
                            const finalPriceNum = Number(p.prixFinal);
                            const calcPriceNum = Number(p.prixCalcule);
                            const priceVal = (!isNaN(finalPriceNum) && finalPriceNum > 0)
                              ? finalPriceNum
                              : ((!isNaN(calcPriceNum) && calcPriceNum > 0) ? calcPriceNum : null);
                            const priceStr = priceVal !== null ? `${priceVal.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT` : 'Prix non défini';
                            return (
                              <option key={p._id} value={p._id}>
                                {p.nom} — {powerStr} — {priceStr}
                              </option>
                            );
                          })}
                        </select>

                        {formData.packId ? (() => {
                          const p = packsList.find(item => String(item._id) === String(formData.packId));
                          return p ? (
                            <div className="pack-summary-badge">
                              <strong>Pack Sélectionné :</strong> {p.nom}<br />
                              • <strong>Puissance auto-renseignée :</strong> {formData.puissance} kWc<br />
                              • <strong>Tarif total auto-renseigné :</strong> {Number(formData.montantTotalPaiement || 0).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT
                            </div>
                          ) : null;
                        })() : (
                          <div className="manual-mode-badge">
                            <strong>Mode Saisie Manuelle :</strong> Renseignez directement la puissance et le tarif ci-dessous.
                          </div>
                        )}
                      </div>

                      {/* Option Pompage */}
                      <div className="form-group form-grid-full" style={{ background: '#f0f9ff', padding: '12px 16px', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0, color: '#0369a1', fontWeight: '600' }}>
                          <input
                            type="checkbox"
                            id="pompage"
                            name="pompage"
                            checked={formData.pompage}
                            onChange={(e) => {
                              const isPompage = e.target.checked;
                              setFormData(prev => ({
                                ...prev,
                                pompage: isPompage,
                                typePaiement: isPompage ? 'comptant' : prev.typePaiement
                              }));
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          Dossier Pompage Solaire (Paiement 100% Comptant, référence STEG optionnelle)
                        </label>
                      </div>

                      {/* Référence STEG & Puissance */}
                      <div className="form-group">
                        <label>Référence STEG {!formData.pompage && <span className="required-star">*</span>}</label>
                        <input
                          type="text"
                          name="referenceSteg"
                          value={formData.referenceSteg}
                          onChange={handleInputChange}
                          required={!formData.pompage}
                          placeholder={formData.typeClient === 'MT' ? '6 chiffres (ex: 123456)' : '9 chiffres (ex: 123456789)'}
                          className={fieldErrors?.referenceSteg ? 'input-has-error' : ''}
                        />
                        {fieldErrors?.referenceSteg && <span className="field-error-text">{fieldErrors.referenceSteg}</span>}
                      </div>

                      <div className="form-group">
                        <label>Puissance (kWc) <span className="required-star">*</span></label>
                        <input
                          type="text"
                          inputMode="decimal"
                          name="puissance"
                          value={formData.puissance}
                          onChange={handleInputChange}
                          required
                          placeholder="ex: 3.5"
                          className={fieldErrors?.puissance ? 'input-has-error' : ''}
                        />
                        {fieldErrors?.puissance && <span className="field-error-text">{fieldErrors.puissance}</span>}
                      </div>

                      {/* Catégorie & Bureau STEG */}
                      <div className="form-group">
                        <label>Catégorie STEG (Région)</label>
                        <select
                          name="_categorieSteg"
                          value={formData._categorieSteg || ''}
                          onChange={handleCategorieStegChange}
                          className="notranslate"
                          translate="no"
                        >
                          <option value="">— Sélectionner une catégorie —</option>
                          {CATEGORIES_STEG.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Bureau STEG (District)</label>
                        <select
                          name="district"
                          value={formData.district}
                          onChange={handleInputChange}
                          disabled={!formData._categorieSteg || loadingSteg}
                          className="notranslate"
                          translate="no"
                        >
                          <option value="">— Sélectionner un bureau —</option>
                          {stegsByCategorie.map(s => (
                            <option key={s._id || s.region} value={s._id}>{s.region}{s.numTel ? ` · ${s.numTel}` : ''}</option>
                          ))}
                        </select>
                      </div>

                      {/* Commercial */}
                      <div className="form-group">
                        <label>Commercial</label>
                        <div style={{position:'relative'}}>
                          <input
                            type="text"
                            placeholder="🔍 Rechercher un commercial..."
                            value={commercialAddSearch}
                            onChange={(e) => {
                              setCommercialAddSearch(e.target.value);
                              if (!e.target.value) setFormData(prev => ({ ...prev, commercial: '' }));
                            }}
                            style={{width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:'8px', fontSize:'0.9375rem', color:'#1e293b', background:'#f8fafc', boxSizing:'border-box'}}
                          />
                          {formData.commercial && (
                            <span
                              style={{position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', color:'#10b981', fontWeight:'700', fontSize:'16px', cursor:'pointer', lineHeight:1}}
                              onClick={() => { setFormData(prev => ({ ...prev, commercial: '' })); setCommercialAddSearch(''); }}
                              title="Effacer"
                            >
                              ✕
                            </span>
                          )}
                          {commercialAddSearch && !formData.commercial && (
                            <div style={{position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1.5px solid #3b82f6', borderTop:'none', borderRadius:'0 0 8px 8px', maxHeight:'200px', overflowY:'auto', zIndex:300, boxShadow:'0 4px 16px rgba(0,0,0,0.12)'}}>
                              <div
                                style={{padding:'9px 14px', cursor:'pointer', fontSize:'13px', color:'#6b7280', borderBottom:'1px solid #f1f5f9'}}
                                onClick={() => { setFormData(prev => ({ ...prev, commercial: '' })); setCommercialAddSearch(''); }}
                              >
                                — Aucun commercial —
                              </div>
                              {commerciauxList
                                .filter(c => `${c.prenom} ${c.nom}`.toLowerCase().includes(commercialAddSearch.toLowerCase()))
                                .map(c => (
                                  <div
                                    key={c._id}
                                    style={{padding:'9px 14px', cursor:'pointer', fontSize:'13px', color:'#1e293b', borderBottom:'1px solid #f9fafb'}}
                                    onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
                                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                                    onClick={() => { setFormData(prev => ({ ...prev, commercial: c._id })); setCommercialAddSearch(`${c.prenom} ${c.nom}`); }}
                                  >
                                    <strong>{c.prenom} {c.nom}</strong>
                                    {c.numeroTelephone && <span style={{marginLeft:'8px', fontSize:'11px', color:'#6b7280'}}>{c.numeroTelephone}</span>}
                                  </div>
                                ))
                              }
                              {commerciauxList.filter(c => `${c.prenom} ${c.nom}`.toLowerCase().includes(commercialAddSearch.toLowerCase())).length === 0 && (
                                <div style={{padding:'9px 14px', fontSize:'12px', color:'#9ca3af', fontStyle:'italic'}}>Aucun résultat pour "{commercialAddSearch}"</div>
                              )}
                            </div>
                          )}
                        </div>
                        {formData.commercial && (
                          <small style={{color:'#10b981', fontSize:'0.78rem', marginTop:'4px', display:'block'}}>
                            ✓ Sélectionné : {commerciauxList.find(c => String(c._id) === String(formData.commercial)) ? `${commerciauxList.find(c => String(c._id) === String(formData.commercial)).prenom} ${commerciauxList.find(c => String(c._id) === String(formData.commercial)).nom}` : ''}
                          </small>
                        )}
                      </div>

                      {/* Chef d'équipe & État initial */}
                      <div className="form-group">
                        <label>Chef d'équipe (Installateur)</label>
                        <select
                          value={formData.installateur}
                          onChange={(e) => setFormData({ ...formData, installateur: e.target.value })}
                        >
                          <option value="">— Aucun chef d'équipe —</option>
                          {installateursList.map(i => (
                            <option key={i._id} value={i._id}>{i.prenom} {i.nom} {i.isEquipe ? `(Équipe: ${i.typeEquipe})` : ''}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>État Initial du Dossier</label>
                        <input
                          type="text"
                          value="Déposé chez Sastra"
                          disabled
                          style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b', fontWeight: '600' }}
                        />
                      </div>

                      {/* Dates & Période */}
                      <div className="form-group">
                        <label>Date Arrivée Dossier</label>
                        <input type="date" name="dateArrivDossier" value={formData.dateArrivDossier} onChange={handleInputChange} />
                      </div>

                      <div className="form-group">
                        <label>Période (Mois / Année)</label>
                        <input type="text" name="moisAnnee" value={formData.moisAnnee} disabled style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Étape 3 : Finance & Règlements */}
                {currentStep === 3 && (
                  <div className="fade-in step-three-compact">
                    <div className="form-section-header">
                      <h3 className="form-section-title">3. Modèle Financier & Sélection du Financement STEG</h3>
                      <p className="form-section-desc">Définissez le montant total du projet et sélectionnez le crédit STEG (5 000, 6 500, 7 500, 10 000 DT) avec calcul automatique de l'autofinancement.</p>
                    </div>

                    <div className="form-grid-2col">
                      <div className="form-group">
                        <label>Montant Total Projet (TND) <span className="required-star">*</span></label>
                        <input
                          type="text"
                          inputMode="decimal"
                          min="0"
                          name="montantTotalPaiement"
                          value={formData.montantTotalPaiement}
                          onChange={handleInputChange}
                          required
                          placeholder="ex: 10000"
                          className={fieldErrors?.montantTotalPaiement ? 'input-has-error' : ''}
                        />
                        {fieldErrors?.montantTotalPaiement && <span className="field-error-text">{fieldErrors.montantTotalPaiement}</span>}
                      </div>

                      <div className="form-group">
                        <label>Montant Crédit STEG <span className="required-star">*</span></label>
                        <select
                          name="montantSteg"
                          value={formData.montantSteg !== undefined && formData.montantSteg !== null ? formData.montantSteg : ''}
                          onChange={handleInputChange}
                          required
                          disabled={formData.pompage}
                        >
                          <option value="0">Sélectionner le crédit STEG (0 DT - Comptant)</option>
                          {getStegOptionsList(formData.montantTotalPaiement, formData.montantSteg).map(opt => (
                            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Autofinancement (Calculé DT)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={repartitionPreview.montantAutofinancement}
                          readOnly
                          style={{ backgroundColor: '#f1f5f9', color: '#1e293b', fontWeight: '600' }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Type de Paiement Déterminé</label>
                        <input
                          type="text"
                          value={
                            repartitionPreview.typePaiement === 'credit' ? 'Crédit STEG 100%' :
                            repartitionPreview.typePaiement === 'mixte' ? 'Paiement Mixte (STEG + Autofinancement)' :
                            'Paiement 100% Comptant'
                          }
                          readOnly
                          style={{ backgroundColor: '#f1f5f9', color: '#0f172a', fontWeight: '600' }}
                        />
                      </div>

                      {/* Case à cocher Acompte versé */}
                      <div className="form-group form-grid-full" style={{ marginTop: '6px', marginBottom: '6px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600', color: '#1e293b' }}>
                          <input
                            type="checkbox"
                            name="acompteVerse"
                            checked={!!formData.acompteVerse}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const today = new Date().toISOString().split('T')[0];
                              setFormData(prev => ({
                                ...prev,
                                acompteVerse: checked,
                                montantComptant: checked ? prev.montantComptant : '',
                                datePaiementComptant: checked ? (prev.datePaiementComptant || today) : today,
                                modePaiementComptant: checked ? (prev.modePaiementComptant || 'especes') : 'especes'
                              }));
                            }}
                            style={{ width: '18px', height: '18px', accentColor: '#2563eb', cursor: 'pointer' }}
                          />
                          <span>Acompte versé</span>
                        </label>
                      </div>

                      {/* Champs Acompte (affichés uniquement si Acompte versé est coché) */}
                      {formData.acompteVerse && (
                        <>
                          <div className="form-group">
                            <label>Montant de l'acompte (TND) <span className="required-star">*</span></label>
                            <input
                              type="text"
                              inputMode="decimal"
                              min="0"
                              name="montantComptant"
                              value={formData.montantComptant}
                              onChange={handleInputChange}
                              placeholder="ex: 1000"
                              className={fieldErrors?.montantComptant ? 'input-has-error' : ''}
                            />
                            {fieldErrors?.montantComptant && <span className="field-error-text">{fieldErrors.montantComptant}</span>}
                          </div>

                          <div className="form-group">
                            <label>Date du paiement <span className="required-star">*</span></label>
                            <input
                              type="date"
                              name="datePaiementComptant"
                              value={formData.datePaiementComptant || ''}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="form-group form-grid-full">
                            <label>Mode de Paiement de l'Acompte <span className="required-star">*</span></label>
                            <select
                              name="modePaiementComptant"
                              value={formData.modePaiementComptant || 'especes'}
                              onChange={handleInputChange}
                            >
                              <option value="especes">Espèces</option>
                              <option value="cheque">Chèque</option>
                              <option value="traite">Traite bancaire</option>
                              <option value="virement">Virement bancaire</option>
                              <option value="versement_bancaire">Versement bancaire</option>
                              <option value="carte">Carte bancaire</option>
                              <option value="autre">Autre mode</option>
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Étape 4 : Validation & Récapitulatif Final */}
                {currentStep === 4 && (() => {
                  const totalProjet = Number(formData.montantTotalPaiement) || 0;
                  return (
                    <div className="fade-in step-four-recap">
                      <div className="form-section-header">
                        <h3 className="form-section-title">4. Confirmation & Création du Client</h3>
                        <p className="form-section-desc">Vérifiez attentivement les données saisies avant d'enregistrer le nouveau client et son dossier initial.</p>
                      </div>

                      <div className="recap-grid-v3">
                        {/* Section 1 : Client */}
                        <div className="summary-section-card highlight-card-blue">
                          <div className="section-card-header">
                            <span className="section-card-icon">👤</span>
                            <h4>1. Informations Client</h4>
                          </div>
                          <div className="summary-fields-list">
                            <div className="summary-field-item">
                              <span className="field-label">Nom complet :</span>
                              <span className="field-value strong">{formData.nom} {formData.prenom}</span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">Identifiant ({formData.typeIdentifiant.toUpperCase()}) :</span>
                              <span className="field-value">{formData.typeIdentifiant === 'cin' ? (formData.cin || '-') : (formData.passeport || '-')}</span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">Téléphone :</span>
                              <span className="field-value">{formData.telephone || '-'}</span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">Type Client :</span>
                              <span className="field-value badge-blue">{formData.typeClient}</span>
                            </div>
                          </div>
                        </div>

                        {/* Section 2 : Dossier Technique */}
                        <div className="summary-section-card highlight-card-amber">
                          <div className="section-card-header">
                            <span className="section-card-icon">📁</span>
                            <h4>2. Dossier Technique</h4>
                          </div>
                          <div className="summary-fields-list">
                            <div className="summary-field-item">
                              <span className="field-label">Type Dossier :</span>
                              <span className="field-value">{formData.pompage ? 'Pompage Solaire' : 'Système STEG'}</span>
                            </div>
                            {!formData.pompage && (
                              <div className="summary-field-item">
                                <span className="field-label">Réf. STEG :</span>
                                <span className="field-value strong">{formData.referenceSteg || '-'}</span>
                              </div>
                            )}
                            <div className="summary-field-item">
                              <span className="field-label">Puissance :</span>
                              <span className="field-value">{formData.puissance ? `${formData.puissance} kWc` : 'Saisie libre'}</span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">District STEG :</span>
                              <span className="field-value">{stegsByCategorie.find(s => s._id === formData.district)?.region || formData.district || '-'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Section 3 : Commercial & Matériel */}
                        <div className="summary-section-card highlight-card-purple">
                          <div className="section-card-header">
                            <span className="section-card-icon">💼</span>
                            <h4>3. Commercial & Matériel</h4>
                          </div>
                          <div className="summary-fields-list">
                            <div className="summary-field-item">
                              <span className="field-label">Commercial :</span>
                              <span className="field-value">{commerciauxList.find(c => c._id === formData.commercial)?.nom ? `${commerciauxList.find(c => c._id === formData.commercial).nom} ${commerciauxList.find(c => c._id === formData.commercial).prenom || ''}` : 'Non assigné'}</span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">Pack Commercial :</span>
                              <span className="field-value strong">
                                {packsList.find(p => p._id === formData.packId)?.nomPack || 'Aucun (Produits libres)'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Section 4 : Financement */}
                        <div className="summary-section-card highlight-card-emerald">
                          <div className="section-card-header">
                            <span className="section-card-icon">💰</span>
                            <h4>4. Financement</h4>
                          </div>
                          <div className="summary-fields-list">
                            <div className="summary-field-item">
                              <span className="field-label">Montant dossier :</span>
                              <span className="field-value price-tag-emerald">{Number(repartitionPreview.montantTotalPaiement).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT</span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">Crédit STEG :</span>
                              <span className="field-value">{Number(repartitionPreview.montantSteg).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT</span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">Autofinancement :</span>
                              <span className="field-value">{Number(repartitionPreview.montantAutofinancement).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT</span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">Type :</span>
                              <span className="field-value badge-emerald">
                                {repartitionPreview.typePaiement === 'credit' ? 'Crédit STEG 100%' :
                                 repartitionPreview.typePaiement === 'mixte' ? 'Mixte' : 'Comptant'}
                              </span>
                            </div>
                            <div className="summary-field-item">
                              <span className="field-label">Acompte :</span>
                              <span className="field-value strong">
                                {formData.acompteVerse && Number(formData.montantComptant) > 0
                                  ? `${Number(formData.montantComptant).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT`
                                  : 'Aucun acompte versé'}
                              </span>
                            </div>
                            {formData.acompteVerse && Number(formData.montantComptant) > 0 && (
                              <>
                                <div className="summary-field-item">
                                  <span className="field-label">Date :</span>
                                  <span className="field-value">{formData.datePaiementComptant || 'Aujourd\'hui'}</span>
                                </div>
                                <div className="summary-field-item">
                                  <span className="field-label">Mode :</span>
                                  <span className="field-value">
                                    {formData.modePaiementComptant === 'especes' ? 'Espèces' :
                                     formData.modePaiementComptant === 'cheque' ? 'Chèque' :
                                     formData.modePaiementComptant === 'traite' ? 'Traite bancaire' :
                                     formData.modePaiementComptant === 'virement' ? 'Virement bancaire' :
                                     formData.modePaiementComptant === 'versement_bancaire' ? 'Versement bancaire' :
                                     formData.modePaiementComptant === 'carte' ? 'Carte bancaire' : 'Autre mode'}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Navigation & Actions du formulaire */}
                <div className="form-actions-v3">
                  {currentStep > 1 ? (
                    <button type="button" className="btn-secondary-v3" onClick={prevStep} disabled={isSubmittingClient}>
                      {currentStep === 4 ? '← Modifier les informations' : '← Précédent'}
                    </button>
                  ) : <div />}

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="button" className="btn-cancel-v3" onClick={handleCancelAdd} disabled={isSubmittingClient}>
                      Réinitialiser
                    </button>
                    {currentStep < 3 ? (
                      <button
                        type="button"
                        className="btn-primary-v3"
                        onClick={nextStep}
                        disabled={(currentStep === 1 && !!fieldErrors?.cin) || (currentStep === 2 && !!fieldErrors?.referenceSteg)}
                        title={(currentStep === 1 && fieldErrors?.cin) || (currentStep === 2 && fieldErrors?.referenceSteg) ? 'Veuillez corriger les erreurs avant de continuer' : ''}
                      >
                        Suivant →
                      </button>
                    ) : currentStep === 3 ? (
                      <button
                        type="button"
                        className="btn-primary-v3"
                        onClick={nextStep}
                        disabled={!!fieldErrors?.cin || !!fieldErrors?.referenceSteg}
                        style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #112237 100%)', color: '#ffffff' }}
                      >
                        Vérifier les informations →
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-save-v3 btn-confirm-final"
                        onClick={handleAddSubmit}
                        disabled={loading || isSubmittingClient || !!fieldErrors?.cin || !!fieldErrors?.referenceSteg}
                        style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#ffffff', gap: '8px' }}
                      >
                        {isSubmittingClient || loading ? (
                          <>
                            <span className="btn-spinner"></span> Enregistrement en cours...
                          </>
                        ) : (
                          '✓ Confirmer et enregistrer le client'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="filters-container">
          <input
            type="text"
            name="search"
            placeholder="🔍 Rechercher (nom, prénom, CIN, passeport...)"
            value={filters.search}
            onChange={handleFilterChange}
            className="filter-search"
          />

          <select name="typeClient" value={filters.typeClient} onChange={handleFilterChange} className="filter-select">
            <option value="">Tous les Types</option>
            <option value="BT">BT</option>
            <option value="MT">MT</option>
            <option value="BT/MT">BT/MT</option>
          </select>

          {/* 1. Catégorie STEG */}
          <select name="categorie" value={filters.categorie} onChange={handleFilterChange} className="filter-select">
            <option value="">Toutes les Catégories</option>
            {CATEGORIES_STEG.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {/* 2. Région (District STEG) — désactivé tant qu'aucune catégorie n'est choisie, affiche les régions de la catégorie */}
          <select
            name="district"
            value={filters.district}
            onChange={handleFilterChange}
            className="filter-select"
            disabled={!filters.categorie}
            style={{ opacity: !filters.categorie ? 0.5 : 1 }}
          >
            <option value="">Toutes les Régions</option>
            {allStegs
              .filter(s => s.categorie === filters.categorie)
              .slice()
              .sort((a, b) => (a.region || '').localeCompare(b.region || '', 'fr'))
              .map(s => (
                <option key={s._id} value={s._id}>
                  {s.region}
                </option>
              ))}
          </select>

          <select
            name="annee"
            value={filters.annee}
            onChange={(e) => {
              const newAnnee = e.target.value;
              setFilters(prev => ({
                ...prev,
                annee: newAnnee,
                // Si l'année est vidée, réinitialiser aussi le mois
                mois: newAnnee === '' ? '' : prev.mois
              }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="filter-select"
          >
            <option value="">Toutes les Années</option>
            {Array.from({length: 10}, (_, i) => new Date().getFullYear() - 5 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select
            name="mois"
            value={filters.mois}
            onChange={handleFilterChange}
            className="filter-select"
            disabled={!filters.annee}
            style={{opacity: !filters.annee ? 0.5 : 1}}
          >
            <option value="">Tous les Mois</option>
            {MOIS_NOMS.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          {/* États dossier — valeurs extraites de l'enum du modèle Client.js */}
          <select name="etatDossier" value={filters.etatDossier} onChange={handleFilterChange} className="filter-select">
            <option value="">Tous les États</option>
            <option value="Déposé chez Sastra">Déposé chez Sastra</option>
            <option value="En attente d'approbation bureau d'étude">En attente d'approbation B.E.</option>
            <option value="Approuvé bureau d'étude">Approuvé bureau d'étude</option>
            <option value="En attente d'approbation">En attente d'approbation</option>
            <option value="En attente d'installation">En attente d'installation</option>
            <option value="En attente de réception">En attente de réception</option>
            <option value="Réceptionné">Réceptionné</option>
            <option value="Manque PV">Manque PV</option>
            <option value="Dossier en facturation">Dossier en facturation</option>
          </select>

          {/* Bouton Réinitialiser : visible dès qu'au moins un filtre (y compris catégorie) est actif */}
          {(filters.search || filters.typeClient || filters.categorie || filters.district || filters.annee || filters.mois || filters.etatDossier) && (
            <button
              onClick={() => {
                setFilters({ search: '', typeClient: '', categorie: '', district: '', annee: '', mois: '', etatDossier: '' });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="btn-reset-filters"
              title="Réinitialiser tous les filtres"
            >
              ✕ Réinitialiser
            </button>
          )}
        </div>

        <div className="clients-table-container">
          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <table className="clients-table">
              <thead>
                <tr>
                  <th>Identifiant</th>
                  <th>Nom & Prénom</th>
                  <th>Type Client</th>
                  <th>Téléphone</th>
                  <th>District</th>
                  <th>Date arrivée dossier</th>
                  <th>État Dossier</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="clients-empty-cell">
                      Aucun client trouvé
                    </td>
                  </tr>
                ) : (
                  filteredClients.map((client, index) => {
                    const clientNomComplet = `${client.nom || ''} ${client.prenom || ''}`.trim() || '—';
                    return (
                      <tr key={client._id}>
                        <td data-label="Identifiant">
                          {client.cin || client.passeport || '—'}
                        </td>
                        <td data-label="Nom & Prénom" style={{ fontWeight: 700, color: '#1e293b' }}>
                          {clientNomComplet}
                        </td>
                        <td data-label="Type Client">{client.typeClient || 'BT'}</td>
                        <td data-label="Telephone">{client.telephone || '—'}</td>
                      <td data-label="District">
                        {client.dossiers && client.dossiers.length > 0 ? (
                          <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                            {client.dossiers.map((dossier, i) => (
                              <div key={i} style={{whiteSpace: 'nowrap', fontSize: '13px'}}>
                                {dossier.referenceSteg ? dossier.referenceSteg + ' : ' : ''}
                                {dossier.district ? (typeof dossier.district === 'object' ? dossier.district.region : dossier.district) : '-'}
                              </div>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td data-label="Date arrivee dossier">
                        {client.dossiers && client.dossiers.length > 0 ? (
                          <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                            {client.dossiers.map((dossier, i) => (
                              <div key={i} style={{whiteSpace: 'nowrap', fontSize: '13px'}}>
                                {dossier.referenceSteg ? dossier.referenceSteg + ' : ' : ''}
                                {dossier.dateArrivDossier ? new Date(dossier.dateArrivDossier).toLocaleDateString('fr-FR') : '-'}
                              </div>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td data-label="Etat Dossier">
                        {client.dossiers && client.dossiers.length > 0 ? (
                          <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                            {client.dossiers.map((dossier, i) => (
                              <span key={i} className="statut-badge" style={{backgroundColor: '#e5e7eb', color: '#374151', border: '1px solid #d1d5db', whiteSpace: 'normal', wordBreak: 'break-word', fontSize: '11px', padding: '2px 6px', display: 'inline-block', maxWidth: '180px'}}>
                                {dossier.referenceSteg ? dossier.referenceSteg + ' : ' : ''}
                                {dossier.etatDossier || '—'}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="statut-badge" style={{backgroundColor: '#e5e7eb', color: '#374151', border: '1px solid #d1d5db', whiteSpace: 'normal'}}>
                            —
                          </span>
                        )}
                      </td>
                      <td data-label="Actions">
                        <div className="action-buttons">
                          <button className="btn-detail" onClick={() => handleDetail(client)}>Détails</button>
                          {hasPermission('clients', 'update') && (
                            <button className="btn-edit" onClick={() => handleEdit(client)}>Modifier</button>
                          )}
                          {hasPermission('clients', 'delete') && isActionVisible('CLIENT_DELETE') && (
                            <button className="btn-delete" onClick={() => handleDelete(client)}>Supprimer</button>
                          )}

                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
        {/* Pagination - Toujours visible s'il y a des clients pour confirmer sa présence */}
        {pagination.total > 0 && (
          <div className="pagination-container" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px'}}>
            <div className="pagination-limit">
              <label style={{fontSize: '14px', color: '#6b7280'}}>Afficher : </label>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  setPagination(prev => ({...prev, limit: parseInt(e.target.value)}));
                  fetchClients(1); // Retour à la page 1 si on change la limite
                }}
                style={{padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db'}}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span style={{fontSize: '14px', color: '#6b7280', marginLeft: '5px'}}>clients par page</span>
            </div>

            <div className="pagination-controls" style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
              <button
                className="pagination-btn"
                onClick={() => fetchClients(1)}
                disabled={pagination.page === 1}
              >
                ⏮️ Première
              </button>
              <button
                className="pagination-btn"
                onClick={() => fetchClients(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                ⬅️ Précédente
              </button>
              <span className="pagination-info" style={{margin: '0 10px'}}>
                Page {pagination.page} sur {pagination.totalPages || 1} ({pagination.total} clients au total)
              </span>
              <button
                className="pagination-btn"
                onClick={() => fetchClients(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Suivante ➡️
              </button>
              <button
                className="pagination-btn"
                onClick={() => fetchClients(pagination.totalPages)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Dernière ⏭️
              </button>
            </div>
          </div>
        )}

        {/* Modale d'édition — informations client uniquement */}
        {showEditModal && selectedClient && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Modifier le client</h2>
                <button className="modal-close" onClick={closeModals}>×</button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <p style={{marginBottom: '16px', color: '#6b7280', fontSize: '0.875rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '10px 14px'}}>
                  💡 Pour modifier les <strong>dossiers</strong> (district, dates, statut…), cliquez sur <em>Détails</em> du client puis accédez au dossier concerné.
                </p>

                {/* Type client */}
                <div className="form-group">
                  <label>Type Client *</label>
                  <select name="typeClient" value={formData.typeClient} onChange={handleInputChange} required>
                    <option value="BT">BT — Basse Tension (Particulier)</option>
                    <option value="MT">MT — Moyenne Tension (Professionnel)</option>
                    <option value="BT/MT">BT/MT — Basse & Moyenne Tension</option>
                  </select>
                </div>

                {/* Nom + Prénom */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Nom {formData.typeClient === 'MT' ? '(Raison sociale)' : ''} *</label>
                    <input type="text" name="nom" value={formData.nom} onChange={handleInputChange} required placeholder="Nom de famille" style={fieldErrors?.nom ? { border: '1px solid #ef4444' } : {}} />
                    {fieldErrors?.nom && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{fieldErrors.nom}</span>}
                  </div>
                  <div className="form-group">
                    <label>Prénom {formData.typeClient === 'BT' ? '*' : '(optionnel)'}</label>
                    <input type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} required={formData.typeClient === 'BT'} placeholder="Prénom" style={fieldErrors?.prenom ? { border: '1px solid #ef4444' } : {}} />
                    {fieldErrors?.prenom && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{fieldErrors.prenom}</span>}
                  </div>
                </div>

                {/* Identifiant (CIN ou Passeport) modifiable (masqué pour MT) */}
                {formData.typeClient !== 'MT' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Identifiant *</label>
                      <div style={{display:'flex', gap:'16px', marginBottom:'8px'}}>
                        <label style={{display:'flex', alignItems:'center', gap:'6px', cursor:'pointer'}}>
                          <input type="radio" name="typeIdentifiant" value="cin"
                            checked={formData.typeIdentifiant === 'cin'}
                            onChange={handleInputChange} />
                          CIN
                        </label>
                        <label style={{display:'flex', alignItems:'center', gap:'6px', cursor:'pointer'}}>
                          <input type="radio" name="typeIdentifiant" value="passeport"
                            checked={formData.typeIdentifiant === 'passeport'}
                            onChange={handleInputChange} />
                          Passeport
                        </label>
                      </div>
                      {formData.typeIdentifiant === 'cin' && (
                        <>
                          <input
                            type="text" name="cin" value={formData.cin}
                            onChange={handleInputChange}
                            inputMode="numeric" maxLength={8}
                            placeholder="8 chiffres (ex: 05260081)"
                            style={fieldErrors?.cin ? { border: '1px solid #ef4444' } : {}}
                          />
                          {fieldErrors?.cin && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{fieldErrors.cin}</span>}
                        </>
                      )}
                      {formData.typeIdentifiant === 'passeport' && (
                        <>
                          <input
                            type="text" name="passeport" value={formData.passeport}
                            onChange={handleInputChange}
                            maxLength={30}
                            placeholder="Numéro de passeport"
                            style={fieldErrors?.passeport ? { border: '1px solid #ef4444' } : {}}
                          />
                          {fieldErrors?.passeport && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{fieldErrors.passeport}</span>}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Email */}
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="email@exemple.com" style={fieldErrors?.email ? { border: '1px solid #ef4444' } : {}} />
                  {fieldErrors?.email && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{fieldErrors.email}</span>}
                </div>

                {/* Adresse */}
                <div className="form-group">
                  <label>Adresse</label>
                  <input type="text" name="adresse" value={formData.adresse} onChange={handleInputChange} placeholder="Adresse complète" />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                  <button type="submit" className="btn-save" disabled={!!fieldErrors?.cin || !!fieldErrors?.referenceSteg}>💾 Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modale de suppression sensible globale */}
        <SensitiveDeleteModal
          isOpen={showSensitiveDeleteModal}
          onClose={() => setShowSensitiveDeleteModal(false)}
          onConfirm={handleConfirmSensitiveDelete}
          itemType="client"
          itemName={deleteTarget?.name || ''}
          deletionConfig={deletionConfig.client || { enabled: true, mode: 'SOFT_DELETE' }}
          loading={deleteLoading}
        />

        {/* Modale d'import Excel */}
        {
          showImportModal && (
            <div className="modal-overlay">
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Importer des clients depuis Excel</h2>
                  <button className="modal-close" onClick={closeModals}>×</button>
                </div>
                <form onSubmit={handleImportSubmit}>
                  <div className="import-info">
                    <h3>Format du fichier :</h3>
                    <p>Le fichier Excel doit contenir 3 feuilles :</p>
                    <ul>
                      <li><strong>Nouveaux Clients</strong> : clients nouveaux</li>
                      <li><strong>Clients Payées / Installées</strong> : clients avec installation ou payés</li>
                      <li><strong>Clients Réceptionnées</strong> : clients réceptionnés</li>
                    </ul>
                    <p>Colonnes requises : clients, P(kwc), cin, district</p>
                    <p>Colonnes optionnelles : tél, Ref/référence, credit, Autofin/Autofinan, Date DF/Date Réception</p>
                  </div>
                  <div className="form-group">
                    <label>Fichier Excel *</label>
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleFileChange}
                      required
                    />
                  </div>
                  {importResult && (
                    <div className="import-result">
                      <h3>Résultat de l'import :</h3>
                      <p>✅ <strong>{importResult.totalImportes}</strong> clients importés</p>
                      {importResult.erreurs && importResult.erreurs.length > 0 && (
                        <div className="import-errors">
                          <p>⚠️ Erreurs :</p>
                          <ul>
                            {importResult.erreurs.map((err, idx) => (
                              <li key={idx}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="modal-actions">
                    <button type="button" className="btn-cancel" onClick={closeModals}>Fermer</button>
                    <button type="submit" className="btn-save">Importer</button>
                  </div>
                </form>
              </div>
            </div>
          )
        }
      </div>
    </div>
  );
};

export default ClientsManagement;
